import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import axios from "axios";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./CombinedCalendarMain.css";
import {
  Button,
  Modal,
  Box,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Typography,
  Chip,
  Divider,
  IconButton,
  Badge,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  Avatar,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Menu,
  Fab,
  Checkbox,
  Drawer,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  FileCopy as DuplicateIcon,
  Link as ChainIcon,
  BrokenImage as BrokenChainIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Navigation as NavigationIcon,
  Today as TodayIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  Event as EventIcon,
  EventBusy as EventBusyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  EmojiEvents as EmojiEventsIcon,
  Construction as ConstructionIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Tune as TuneIcon,
  CallSplit as CallSplitIcon,
} from "@mui/icons-material";
import { useSpring, animated } from "react-spring";
import { v4 as uuidv4 } from "uuid";
import { styled } from "@mui/material/styles";
import { getToken, base_url } from "../../config/config";
import { Label } from "recharts";
import ANRModal from "./ANRModal2";

// ========== NEW COMPONENT IMPORTS ==========
import CompactTemplateSelector from "./components/CompactTemplateSelector";
import CompactTasksEventsDrawer from "./components/CompactTasksEventsDrawer";
import ChainPreviewOverlay from "./components/ChainPreviewOverlay";
import QuickFiltersBar from "./components/QuickFiltersBar";
import BatchOperationsBar, {
  BatchModeToggle,
} from "./components/BatchOperationsBar";
import TemplateApplyModal from "./TemplateApplyModal";
// ========== END NEW IMPORTS ==========

function getISODateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const ItemTypes = {
  PROCESS: "process",
  TASK: "task",
  EVENT: "event",
};

const EnhancedCalendar = ({
  currentMonth,
  currentYear,
  calendarData = {},
  onDropProcess,
  onDropTask,
  onDropEvent,
  attendanceData = [],
  machines = [],
  updateProcessEmployee,
  updateProcessMachine,
  combinedChains = {},
  toggleChainView,
  brokenChains = {},
  toggleChainBroken,
  handleDuplicateTask,
  handleDuplicateEvent,
  employees = [],
  onDeleteProcess,
  onMonthChange,
  openMengeConfigForProcess,
  projectMengeData,
  onSplitProcess,
  onDateClick,
}) => {
  const [calendarExpanded, setCalendarExpanded] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const today = new Date();

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const startingDay = firstDayOfMonth.getDay();
  const totalDays = new Date(currentYear, currentMonth + 1, 0).getDate();

  const calendarCells = [];

  // Fill initial empty cells
  for (let i = 0; i < startingDay; i++) {
    calendarCells.push(null);
  }

  // Fill days with processes and tasks
  for (let day = 1; day <= totalDays; day++) {
    const date = new Date(currentYear, currentMonth, day);
    const dateKey = getISODateString(date);
    const processes = calendarData[dateKey]?.processes || [];
    const tasks = calendarData[dateKey]?.tasks || [];
    const events = calendarData[dateKey]?.events || [];
    calendarCells.push({ date, processes, tasks, events });
  }

  const handleExpandCollapse = () => {
    setCalendarExpanded(!calendarExpanded);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);

    // When going to full screen mode, also expand the calendar
    if (!isFullScreen) {
      setCalendarExpanded(true);
    }

    // Prevent scrolling on body when in full screen mode
    document.body.style.overflow = !isFullScreen ? "hidden" : "";
  };

  const scrollToToday = () => {
    setIsLoading(true);
    // Find today's cell if it exists
    const todayIndex = calendarCells.findIndex(
      (cell) =>
        cell &&
        cell.date &&
        cell.date.getDate() === today.getDate() &&
        cell.date.getMonth() === today.getMonth() &&
        cell.date.getFullYear() === today.getFullYear()
    );

    if (todayIndex >= 0) {
      setTimeout(() => {
        const todayCell = document.getElementById(`calendar-day-${todayIndex}`);
        if (todayCell) {
          todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        setIsLoading(false);
      }, 100);
    } else {
      setIsLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    if (onMonthChange) {
      const newMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const newYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      onMonthChange(newMonth, newYear);
    }
  };

  const goToNextMonth = () => {
    if (onMonthChange) {
      const newMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const newYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      onMonthChange(newMonth, newYear);
    }
  };

  // Effect to handle ESC key pressing to exit full screen
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && isFullScreen) {
        toggleFullScreen();
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
      // Reset overflow when component unmounts
      document.body.style.overflow = "";
    };
  }, [isFullScreen]);

  return (
    <div className={`calendarContainer ${isFullScreen ? "fullScreen" : ""}`}>
      {isLoading && (
        <div className="calendarLoading">
          <div className="loadingSpinner"></div>
        </div>
      )}

      {/* Exit full screen button */}
      {isFullScreen && (
        <Tooltip title="Exit Full Screen">
          <div className="exitFullScreenButton" onClick={toggleFullScreen}>
            <FullscreenExitIcon />
          </div>
        </Tooltip>
      )}

      {/* Calendar Header with Month/Year and Controls */}
      <div className="calendarHeaderContainer">
        <Typography variant="h5" className="calendarTitle">
          {monthNames[currentMonth]} {currentYear}
        </Typography>
        <div className="calendarControls">
          <div className="calendarControlButtons">
            <Tooltip title="Previous Month">
              <IconButton
                color="inherit"
                onClick={goToPreviousMonth}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Heute">
              <IconButton color="inherit" onClick={scrollToToday} size="small">
                <TodayIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Next Month">
              <IconButton color="inherit" onClick={goToNextMonth} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          </div>
          <div className="viewControls">
            <Tooltip
              title={calendarExpanded ? "Collapse Calendar" : "Expand Calendar"}
            >
              <IconButton
                className={`expandButton ${calendarExpanded ? "expanded" : ""}`}
                onClick={handleExpandCollapse}
                color="inherit"
                size="small"
              >
                {calendarExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullScreen ? "Exit Full Screen" : "Full Screen"}>
              <IconButton
                onClick={toggleFullScreen}
                color="inherit"
                size="small"
              >
                {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Day Names Row */}
      <div className="calendarDayNames">
        {dayNames.map((day, index) => (
          <div
            key={index}
            className={`dayNameCell ${
              index === 0 || index === 6 ? "weekend" : ""
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className={`calendarGrid ${calendarExpanded ? "expanded" : ""}`}>
        {calendarCells.map((cell, index) => (
          <EnhancedCalendarDay
            key={index}
            id={`calendar-day-${index}`}
            date={cell ? cell.date : null}
            processes={cell ? cell.processes : []}
            tasks={cell ? cell.tasks : []}
            events={cell ? cell.events : []}
            onDropProcess={onDropProcess}
            onDropTask={onDropTask}
            onDropEvent={onDropEvent}
            attendanceData={attendanceData}
            machines={machines}
            updateProcessEmployee={updateProcessEmployee}
            updateProcessMachine={updateProcessMachine}
            combinedChains={combinedChains}
            toggleChainView={toggleChainView}
            brokenChains={brokenChains}
            toggleChainBroken={toggleChainBroken}
            handleDuplicateTask={handleDuplicateTask}
            handleDuplicateEvent={handleDuplicateEvent}
            employees={employees}
            onDeleteProcess={onDeleteProcess}
            calendarExpanded={calendarExpanded}
            openMengeConfigForProcess={openMengeConfigForProcess}
            projectMengeData={projectMengeData}
            onSplitProcess={onSplitProcess}
            onDateClick={onDateClick}
          />
        ))}
      </div>

      {/* Calendar Legend */}
      <div className="calendarLegend">
        <div className="legendItem">
          <div
            className="legendColor"
            style={{ backgroundColor: "#1976d2" }}
          ></div>
          <span>Heute</span>
        </div>
        <div className="legendItem">
          <div
            className="legendColor"
            style={{ backgroundColor: "#f5f5f5" }}
          ></div>
          <span>Weekend</span>
        </div>
        <div className="legendItem">
          <div
            className="legendColor"
            style={{ backgroundColor: "#4caf50" }}
          ></div>
          <span>Ziel fallen lassen</span>
        </div>
        <div className="legendItem">
          <div
            className="legendColor"
            style={{ backgroundColor: "#f44336" }}
          ></div>
          <span>Gebrochene Kette</span>
        </div>
      </div>
    </div>
  );
};

// Enhanced Calendar Day Component
const EnhancedCalendarDay = ({
  id,
  date,
  processes = [],
  tasks = [],
  events = [],
  onDropProcess,
  onDropTask,
  onDropEvent,
  attendanceData = [],
  machines = [],
  updateProcessEmployee,
  updateProcessMachine,
  combinedChains = {},
  toggleChainView,
  brokenChains = {},
  toggleChainBroken,
  handleDuplicateTask,
  handleDuplicateEvent,
  employees = [],
  onDeleteProcess,
  calendarExpanded = false,
  openMengeConfigForProcess,
  projectMengeData,
  onSplitProcess,
  onDateClick,
}) => {
  const ref = useRef(null);
  const dateKey = date ? date.toISOString().split("T")[0] : null;
  const [dropAnimation, setDropAnimation] = useState(false);
  const [isDropping, setIsDropping] = useState(false);

  const isToday = date && new Date().toDateString() === date.toDateString();
  const isWeekendDay = date && (date.getDay() === 0 || date.getDay() === 6);

  // Group processes by chainId for organization (define this before it's used)
  const processesByChain = processes.reduce((acc, process) => {
    const chainId = process.chainId || process.id;
    if (!acc[chainId]) {
      acc[chainId] = [];
    }
    acc[chainId].push(process);
    return acc;
  }, {});

  // Sort processes within each chain by their order
  Object.keys(processesByChain).forEach((chainId) => {
    processesByChain[chainId].sort(
      (a, b) => (a.chainOrder || 0) - (b.chainOrder || 0)
    );
  });

  // Determine if any chain on this day is broken
  const isChainBrokenOnDay = processes.some(
    (process) => brokenChains[process.chainId]
  );

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [ItemTypes.PROCESS, ItemTypes.TASK, ItemTypes.EVENT],
    drop: (item, monitor) => {
      console.log("Drop event triggered with:", item);

      if (!date) {
        console.error("Cannot drop on an undefined date");
        return { dropped: false, error: "No date" };
      }

      setIsDropping(true);
      setDropAnimation(true);

      setTimeout(() => {
        setDropAnimation(false);
        setIsDropping(false);
      }, 500);

      try {
        // Check what type of item we're dealing with based on the structure
        if (item.type === ItemTypes.PROCESS && item.process) {
          // Handle process drop
          const processData = item.process;
          onDropProcess(processData, date, processData.chainId);
          return { dropped: true, type: "process" };
        } else if (item.type === ItemTypes.TASK && item.task) {
          // Handle task drop
          const taskData = item.task;
          onDropTask(taskData, date);
          return { dropped: true, type: "task" };
        } else if (item.type === ItemTypes.EVENT && item.event) {
          // Handle event drop
          const eventData = item.event;
          onDropEvent(eventData, date);
          return { dropped: true, type: "event" };
        } else {
          const itemType = item.type;

          if (itemType === ItemTypes.PROCESS) {
            // This handles direct process objects
            onDropProcess(item, date, item.chainId);
            return { dropped: true, type: "legacy-process" };
          } else if (itemType === ItemTypes.TASK) {
            onDropTask(item, date);
            return { dropped: true, type: "legacy-task" };
          } else if (itemType === ItemTypes.EVENT) {
            onDropEvent(item, date);
            return { dropped: true, type: "legacy-event" };
          }
        }
      } catch (error) {
        console.error("Error in drop handler:", error);
        return { dropped: false, error: error.message };
      }

      return { dropped: false, error: "Unknown item type or structure" };
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
    hover: (item, monitor) => {
      if (monitor.isOver({ shallow: true })) {
        // console.log(`Hovering over cell ${id} with item:`, item);
      }
    },
  });

  drop(ref);

  // Debug the drag and drop state when isOver changes
  // Place the React.useEffect hook before any conditional returns
  React.useEffect(() => {
    if (isOver && date) {
      // console.log(`Cell ${id} is being dragged over, canDrop:`, canDrop);
    }
  }, [isOver, canDrop, id, date]);

  if (!date) {
    return <div className="calendarCell"></div>;
  }

  const getAbsentUsersOnDate = (date) => {
    if (!attendanceData || !Array.isArray(attendanceData)) return [];

    const formattedDate = date.toISOString().split("T")[0];
    return attendanceData
      .filter(
        (attendance) => attendance.Date === formattedDate && attendance.Abwesend
      )
      .map((attendance) => attendance.user);
  };

  const absentUsers = getAbsentUsersOnDate(date);

  // Format date display
  const dateDisplay = () => {
    return date.getDate();
  };

  return (
    <div
      ref={ref}
      id={id}
      className={`calendarCell 
        ${isToday ? "today" : ""} 
        ${isWeekendDay ? "weekend" : ""} 
        ${isOver && canDrop ? "dragOver" : ""} 
        ${isOver && !canDrop ? "dragOverInvalid" : ""}
        ${dropAnimation ? "dropping" : ""}`}
      style={{
        position: "relative",
        outline: isOver ? "2px dashed #4caf50" : "none",
        transition: "all 0.2s ease",
      }}
    >
      <div
        className={`dayHeader ${isToday ? "today" : ""} ${
          isWeekendDay ? "weekend" : ""
        }`}
        onClick={() => date && onDateClick && onDateClick(date)}
        style={{ cursor: date && onDateClick ? "pointer" : "default" }}
      >
        <div className="dayDate">
          {isToday ? (
            <div className="todayIndicator">{dateDisplay()}</div>
          ) : (
            <span>{dateDisplay()}</span>
          )}
          {date && onDateClick && (
            <Tooltip title="Click to add template">
              <AddIcon
                sx={{
                  fontSize: 16,
                  ml: 0.5,
                  opacity: 0.6,
                  "&:hover": { opacity: 1 },
                }}
              />
            </Tooltip>
          )}
        </div>
        {isChainBrokenOnDay && (
          <Tooltip title="Chain broken">
            <WarningIcon color="error" fontSize="small" />
          </Tooltip>
        )}
      </div>

      {absentUsers && absentUsers.length > 0 && (
        <Chip
          icon={<EventBusyIcon />}
          label={`Abwesend: ${absentUsers.join(", ")}`}
          size="small"
          color="error"
          variant="outlined"
          sx={{ mt: 0.5, width: "100%", justifyContent: "flex-start" }}
        />
      )}

      <div className={`calendarContent ${dropAnimation ? "dropped" : ""}`}>
        {/* Display Processes */}
        {Object.keys(processesByChain).map((chainId) => {
          const chainProcesses = processesByChain[chainId];
          const isCombined = combinedChains[chainId];
          const isChainBroken = brokenChains[chainId];

          if (isCombined) {
            // Show combined view
            const firstProcess = chainProcesses[0];
            const lastProcess = chainProcesses[chainProcesses.length - 1];

            if (!calendarExpanded) {
              // Compact view for chains
              return (
                <div
                  key={chainId}
                  className={`processBox compact ${
                    isChainBroken ? "brokenChain" : ""
                  }`}
                  style={{
                    borderLeftColor:
                      processColors[firstProcess.id] || "#1976d2",
                    backgroundColor: isChainBroken
                      ? "rgba(244, 67, 54, 0.1)"
                      : undefined,
                  }}
                >
                  <div className="processHeader">
                    <div
                      className="processTitle"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <ChainIcon
                        fontSize="small"
                        style={{ marginRight: "4px", opacity: 0.7 }}
                      />
                      {firstProcess.anr || "ANR Not Assigned"}
                    </div>
                  </div>
                </div>
              );
            } else {
              // Expanded view for chains
              return (
                <div
                  key={chainId}
                  className={`chainContainer ${
                    isChainBroken ? "brokenChain" : ""
                  }`}
                >
                  <div
                    className={`chainHeader ${
                      isChainBroken ? "brokenChainHeader" : ""
                    }`}
                  >
                    <span>{firstProcess.anr || "ANR Not Assigned"}</span>
                    <Tooltip title="Toggle Chain View">
                      <IconButton
                        size="small"
                        onClick={() => toggleChainView(chainId)}
                        sx={{ color: "white", padding: "2px" }}
                      >
                        <ChainIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {firstProcess.auftragsbezeichnung}
                    </Typography>
                    <Typography variant="caption">
                      {`${firstProcess.scheduledDate.toLocaleDateString()} - ${lastProcess.scheduledDate.toLocaleDateString()}`}
                    </Typography>

                    {/* Display assigned resources in expanded view */}
                    {firstProcess.assignedEmployees &&
                      firstProcess.assignedEmployees.length > 0 && (
                        <div
                          className="employeeMachineInfo"
                          style={{ marginTop: "8px" }}
                        >
                          <div className="employeeInfo">
                            <PeopleIcon className="infoIcon" />
                            <span className="infoLabel">Team:</span>
                            <span>
                              {firstProcess.assignedEmployees
                                .map((a) => a.employee?.name)
                                .join(", ") || "keine Mitarbeiter zugewiesen."}
                            </span>
                          </div>
                        </div>
                      )}
                  </CardContent>
                </div>
              );
            }
          } else {
            // Show processes as a connected chain
            return (
              <div key={chainId} className="process-chain-container">
                {chainProcesses.map((process, index) => (
                  <React.Fragment key={process.id}>
                    <DraggableProcess
                      process={process}
                      calendarExpanded={calendarExpanded}
                      toggleChainBroken={toggleChainBroken}
                      isChainBroken={isChainBroken}
                      onDeleteProcess={onDeleteProcess}
                      onSplitProcess={onSplitProcess}
                      processColor={processColors[process.id] || "#1976d2"}
                      isInChain={true}
                      isFirst={index === 0}
                      isLast={index === chainProcesses.length - 1}
                    />
                    {index < chainProcesses.length - 1 && (
                      <div className="process-connector">
                        <svg height="10" width="20">
                          <line
                            x1="0"
                            y1="5"
                            x2="20"
                            y2="5"
                            style={{
                              stroke: isChainBroken ? "#f44336" : "#1976d2",
                              strokeWidth: 2,
                              strokeDasharray: isChainBroken ? "3,3" : "none",
                            }}
                          />
                          <polygon
                            points="15,2 20,5 15,8"
                            style={{
                              fill: isChainBroken ? "#f44336" : "#1976d2",
                            }}
                          />
                        </svg>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            );
          }
        })}

        {/* Display Tasks using the DraggableTask component */}
        {tasks.map((task) => (
          <DraggableTask
            key={task.id}
            task={task}
            calendarExpanded={calendarExpanded}
            handleDuplicateTask={handleDuplicateTask}
          />
        ))}

        {/* Display Events using the DraggableEvent component */}
        {events.map((event, i) => (
          <DraggableEvent
            key={`${event.id}-${i}`} // 🔑 ← unique per entry
            event={event}
            calendarExpanded={calendarExpanded}
            handleDuplicateEvent={handleDuplicateEvent}
          />
        ))}

        {/* Empty state */}
        {processes.length === 0 &&
          tasks.length === 0 &&
          events.length === 0 && (
            <div className="emptyDayIndicator">Keine Artikel geplant</div>
          )}
      </div>
    </div>
  );
};

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: "100%",
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const StyledCard = styled(Card)(({ theme, color, selected }) => ({
  marginBottom: theme.spacing(1.5),
  borderLeft: `4px solid ${color || theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: selected
    ? `0 0 0 2px ${theme.palette.primary.main}`
    : "0 2px 4px rgba(0, 0, 0, 0.1)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
  },
}));

const FloatingActionButton = styled(Button)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  borderRadius: "28px",
  padding: theme.spacing(1.5, 3),
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 14px rgba(0, 0, 0, 0.25)",
    transform: "translateY(-2px)",
  },
}));

const TemplateCard = styled(Card)(({ theme, selected }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: selected ? `2px solid ${theme.palette.primary.main}` : "none",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
}));

// Define colors for processes (ensure unique keys)
const processColors = {
  1: "#1976d2", // Blue
  2: "#ff9800", // Orange
  3: "#2e7d32", // Green
  4: "#d32f2f", // Red
  5: "#7b1fa2", // Purple
  6: "#0288d1", // Light Blue
  7: "#ef6c00", // Dark Orange
  8: "#00796b", // Teal
  9: "#c2185b", // Pink
  10: "#303f9f", // Indigo
  11: "#f57c00", // Orange
  12: "#388e3c", // Green
  13: "#1565c0", // Blue
  14: "#5e35b1", // Deep Purple
  15: "#d81b60", // Pink
  16: "#00acc1", // Cyan
  17: "#43a047", // Green
};

// Define German month names and day names
const monthNames = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

const DraggableProcess = memo(
  ({
    process,
    calendarExpanded,
    toggleChainBroken,
    isChainBroken,
    onDeleteProcess,
    onSplitProcess,
    processColor,
    isInChain = false,
    isFirst = false,
    isLast = false,
  }) => {
    const [{ isDragging }, drag] = useDrag(
      () => ({
        type: ItemTypes.PROCESS,
        item: () => {
          console.log("Started dragging calendar process:", process);
          return {
            type: ItemTypes.PROCESS,
            process: {
              ...process,
              id: process.originalProcessId || process.id, // Use original ID for multi-day processes
              chainId: process.chainId || null,
              scheduledDate: process.scheduledDate,
              // Add unique identifier for same-name processes
              uniqueId:
                process.calendarId ||
                `${process.id}-${process.chainId}-${Date.now()}`,
            },
          };
        },
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
      }),
      [process]
    );

    // Create a unique key for React rendering
    const uniqueKey =
      process.calendarId ||
      `${process.id}-${process.chainId}-${
        process.chainOrder
      }-${process.scheduledDate?.getTime()}`;

    // For minimized view, only show ANR with color if not expanded
    if (!calendarExpanded) {
      return (
        <div
          ref={drag}
          key={uniqueKey}
          className={`processBox compact ${isDragging ? "dragging" : ""} ${
            isInChain ? "chain-item" : ""
          } ${isFirst ? "chain-first" : ""} ${isLast ? "chain-last" : ""} ${
            process.isMultiDay ? "multi-day" : ""
          }`}
          style={{
            borderLeftColor: processColor || "#1976d2",
            opacity: isDragging ? 0.5 : 1,
            cursor: "move",
            backgroundColor: isChainBroken
              ? "rgba(244, 67, 54, 0.1)"
              : undefined,
          }}
        >
          <div className="processHeader">
            <div
              className="processTitle"
              style={{ display: "flex", alignItems: "center" }}
            >
              {isInChain && (
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: processColor || "#1976d2",
                    marginRight: "4px",
                  }}
                ></span>
              )}
              {process.anr || process.label || "Process"}
              {process.isMultiDay && (
                <span
                  style={{
                    fontSize: "10px",
                    marginLeft: "4px",
                    color: "#666",
                    fontWeight: "normal",
                  }}
                >
                  ({process.dayNumber}/{process.totalDays})
                </span>
              )}
              {/* Add chain order indicator for same-name processes */}
              {process.chainOrder !== undefined && (
                <span
                  style={{
                    fontSize: "9px",
                    marginLeft: "4px",
                    color: "#999",
                    fontWeight: "normal",
                  }}
                >
                  #{process.chainOrder + 1}
                </span>
              )}
            </div>
          </div>
          {process.isMultiDay && (
            <div
              style={{
                fontSize: "10px",
                color: "#666",
                marginTop: "2px",
                textAlign: "center",
              }}
            >
              {process.hours}h Heute
            </div>
          )}
        </div>
      );
    }

    // Expanded view with more details
    return (
      <div
        ref={drag}
        key={uniqueKey}
        className={`processBox ${isDragging ? "dragging" : ""} ${
          isInChain ? "chain-item" : ""
        } ${isFirst ? "chain-first" : ""} ${isLast ? "chain-last" : ""} ${
          process.isMultiDay ? "multi-day" : ""
        }`}
        style={{
          borderLeftColor: processColor || "#1976d2",
          opacity: isDragging ? 0.5 : 1,
          cursor: "move",
          backgroundColor: isChainBroken ? "rgba(244, 67, 54, 0.1)" : undefined,
        }}
      >
        <div className="processHeader">
          <div className="processTitle">
            {process.anr || process.label || "Process"}
            {process.isMultiDay && (
              <span
                style={{
                  fontSize: "12px",
                  marginLeft: "8px",
                  color: "#666",
                  fontWeight: "normal",
                }}
              >
                (Day {process.dayNumber}/{process.totalDays})
              </span>
            )}
            {/* Show chain position for same-name processes */}
            {process.chainOrder !== undefined && (
              <span
                style={{
                  fontSize: "11px",
                  marginLeft: "8px",
                  color: "#1976d2",
                  fontWeight: "bold",
                }}
              >
                Step {process.chainOrder + 1}
              </span>
            )}
          </div>
          <div className="processActions">
            <Tooltip title="Split into Half Days">
              <IconButton
                size="small"
                onClick={() => onSplitProcess?.(process)}
                className="actionButton"
              >
                <CallSplitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Toggle Chain Status">
              <IconButton
                size="small"
                onClick={() => toggleChainBroken(process.chainId)}
                className="actionButton"
              >
                {isChainBroken ? (
                  <BrokenChainIcon fontSize="small" />
                ) : (
                  <ChainIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Process">
              <IconButton
                size="small"
                onClick={() => onDeleteProcess(process)}
                className="actionButton"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div className="processDetails">
          <div>{process.auftragsbezeichnung || process.description}</div>

          {process.isMultiDay && (
            <div
              style={{
                backgroundColor: "#e3f2fd",
                padding: "4px 8px",
                borderRadius: "4px",
                margin: "4px 0",
                fontSize: "12px",
              }}
            >
              <strong>Multi-day Process:</strong> {process.hours}h scheduled for
              Heute
              <br />
              <span style={{ color: "#666" }}>
                Gesamt: {process.totalDays * 8 - (8 - (process.hours % 8 || 8))}
                h über {process.totalDays} Tage
              </span>
            </div>
          )}

          <div className="employeeMachineInfo">
            {process.assignedEmployees &&
              process.assignedEmployees.length > 0 && (
                <div className="employeeInfo">
                  <PeopleIcon className="infoIcon" />
                  <span className="infoLabel">Mitarbeiter:</span>
                  <span>
                    {process.assignedEmployees[0].employee?.name ||
                      "keine Mitarbeiter zugewiesen."}
                  </span>
                </div>
              )}

            {process.assignedEmployees &&
              process.assignedEmployees.length > 0 &&
              process.assignedEmployees[0].machine && (
                <div className="machineInfo">
                  <BuildIcon className="infoIcon" />
                  <span className="infoLabel">Maschine:</span>
                  <span>
                    {process.assignedEmployees[0].machine.name ||
                      "keine Maschine zugewiesen."}
                  </span>
                </div>
              )}

            {(!process.assignedEmployees ||
              process.assignedEmployees.length === 0) && (
              <div className="employeeInfo">
                <PeopleIcon className="infoIcon" />
                <span className="infoLabel">Mitarbeiter:</span>
                <span>Nicht zugewiesen</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

const calculateChainOffsets = (chainProcesses, existingCalendarData = {}) => {
  const offsets = new Map();
  const sortedProcesses = [...chainProcesses].sort(
    (a, b) => (a.chainOrder || 0) - (b.chainOrder || 0)
  );

  if (sortedProcesses.length === 0) return offsets;

  // Find currently scheduled dates for chain processes
  const scheduledDates = new Map();
  Object.keys(existingCalendarData).forEach((dateKey) => {
    const dayData = existingCalendarData[dateKey];
    if (dayData && dayData.processes) {
      dayData.processes.forEach((process) => {
        const processId = process._id || process.id;
        const instanceId = process.instanceId || "";
        if (sortedProcesses.some((p) => (p._id || p.id) === processId)) {
          scheduledDates.set(processId + instanceId, new Date(dateKey));
        }
      });
    }
  });

  if (scheduledDates.size >= 2) {
    const baseProcess = sortedProcesses[0];
    const baseProcessId = baseProcess._id || baseProcess.id;
    const baseProcessinstanceId = baseProcess.instanceId || "";
    const baseDate = scheduledDates.get(baseProcessId + baseProcessinstanceId);

    if (baseDate) {
      sortedProcesses.forEach((process) => {
        const processId = process._id || process.id;
        const processinstanceId = process.instanceId || "";
        const key = processId + processinstanceId;
        const scheduledDate = scheduledDates.get(key);

        if (scheduledDate) {
          const workingDaysOffset = getWorkingDaysBetween(
            baseDate,
            scheduledDate
          );
          offsets.set(key, workingDaysOffset);
        } else {
          // Use chain order as fallback
          offsets.set(key, process.chainOrder || 0);
        }
      });
    }
  } else {
    // No existing schedule - use sequential offsets with proper gaps
    sortedProcesses.forEach((process, index) => {
      const defaultOffset = index;
      const baseProcessinstanceId = process.instanceId || "";
      const key = (process._id || process.id) + baseProcessinstanceId;
      offsets.set(key, defaultOffset);
    });
  }

  return offsets;
};

// Enhanced chain process scheduling with preserved internal offsets
const scheduleProcessesAroundProcess = (
  processes,
  droppedProcess,
  dropDate,
  calendarData = {}
) => {
  if (!processes || processes.length === 0) {
    console.warn("Keine Prozesse für Chain-Scheduling bereitgestellt");
    return {
      [droppedProcess._id || droppedProcess.id]: {
        date: new Date(dropDate),
        hours: droppedProcess.hours || 8,
      },
    };
  }

  const droppedProcessId = droppedProcess._id || droppedProcess.id;
  const sortedProcesses = [...processes].sort(
    (a, b) => (a.chainOrder || 0) - (b.chainOrder || 0)
  );

  // Find the index of the dropped process
  const droppedIndex = sortedProcesses.findIndex(
    (p) => (p._id || p.id) === droppedProcessId
  );

  if (droppedIndex === -1) {
    console.warn(
      "Dropped process not found in chain, scheduling only this process"
    );
    return {
      [droppedProcessId]: {
        date: new Date(dropDate),
        hours: droppedProcess.hours || 8,
      },
    };
  }

  const chainOffsets = calculateChainOffsets(sortedProcesses);
  // Ensure drop date is a working day
  const workingDropDate = ensureWorkingDay(new Date(dropDate));

  let scheduledDates = {};
  let occupiedDate = [];

  const originalDroppedOffset =
    chainOffsets.get(droppedProcessId) || droppedIndex;
  // Schedule all processes while preserving their internal offsets
  sortedProcesses.forEach((process, index) => {
    const processId = process._id || process.id;
    const instanceId = process.instanceId || "";
    const originalOffset = chainOffsets.get(processId + instanceId) || index;

    // Calculate the working day offset from the dropped process
    const relativeOffset = originalOffset - originalDroppedOffset;

    // Calculate the target date using working days
    let targetDate;
    if (relativeOffset === 0) {
      // This is the dropped process
      targetDate = new Date(workingDropDate);
    } else if (relativeOffset < 0) {
      // Process comes before the dropped process
      targetDate = addWorkingDays(
        workingDropDate,
        relativeOffset,
        occupiedDate
      ); // relativeOffset is negative
    } else {
      // Process comes after the dropped process
      targetDate = addWorkingDays(
        workingDropDate,
        relativeOffset,
        occupiedDate
      );
    }
    occupiedDate.push(getISODateString(targetDate));

    // Handle multi-day processes
    const processHours = process.hours || 8;
    if (processHours > 8) {
      const daysNeeded = Math.ceil(processHours / 8);
      let remainingHours = processHours;

      for (let day = 0; day < daysNeeded; day++) {
        const hoursForThisDay = Math.min(8, remainingHours);
        const dayDate = addWorkingDays(targetDate, day);
        const dayProcessId =
          day === 0 ? processId : `${processId}_day${day + 1}`;

        scheduledDates[dayProcessId] = {
          date: dayDate,
          hours: hoursForThisDay,
          originalProcessId: processId,
          dayNumber: day + 1,
          totalDays: daysNeeded,
          isMultiDay: true,
          chainOffset: relativeOffset + day,
          process: {
            ...process,
            hours: hoursForThisDay,
            isMultiDay: true,
            dayNumber: day + 1,
            totalDays: daysNeeded,
            originalProcessId: processId,
            calendarId: `${processId}_day${day + 1}_${Date.now()}`,
          },
        };

        remainingHours -= hoursForThisDay;
      }
    } else {
      // Single day process
      scheduledDates[processId + instanceId] = {
        date: targetDate,
        hours: processHours,
        chainOffset: relativeOffset,
        process: {
          ...process,
          hours: processHours,
          isMultiDay: false,
          dayNumber: 1,
          totalDays: 1,
          calendarId: `${processId + instanceId}_${Date.now()}`,
        },
      };
    }
  });

  console.log("✅ Chain scheduling complete with preserved offsets");
  return scheduledDates;
};

const detectResourceConflicts = (scheduledDates, calendarData) => {
  const conflicts = [];
  const dateHours = {};

  // Calculate total hours per date from scheduled processes
  Object.entries(scheduledDates).forEach(([processId, scheduleInfo]) => {
    const dateKey = scheduleInfo.date.toISOString().split("T")[0];
    dateHours[dateKey] = (dateHours[dateKey] || 0) + scheduleInfo.hours;
  });

  // Check for conflicts with existing calendar data
  Object.entries(scheduledDates).forEach(([processId, scheduleInfo]) => {
    const dateKey = scheduleInfo.date.toISOString().split("T")[0];
    const dayData = calendarData[dateKey];

    let totalHours = scheduleInfo.hours;
    if (dayData && dayData.processes) {
      totalHours += dayData.processes.reduce(
        (sum, p) => sum + (p.hours || 0),
        0
      );
    }

    if (totalHours > 8) {
      conflicts.push({
        processId,
        date: scheduleInfo.date,
        conflictingProcesses: dayData?.processes || [],
        totalHours,
        overageHours: totalHours - 8,
      });
    }
  });

  return conflicts;
};
// Resolve conflicts by rescheduling
const resolveResourceConflicts = (
  scheduledDates,
  calendarData,
  maxRetries = 5
) => {
  let resolvedDates = { ...scheduledDates };
  let attempts = 0;

  while (attempts < maxRetries) {
    const conflicts = detectResourceConflicts(resolvedDates, calendarData);

    if (conflicts.length === 0) {
      console.log("✅ All resource conflicts resolved");
      break;
    }

    console.log(
      `⚠️ Attempt ${attempts + 1}: Resolving ${conflicts.length} conflicts`
    );

    // Sort conflicts by severity (most overloaded first)
    conflicts.sort((a, b) => b.overageHours - a.overageHours);

    conflicts.forEach((conflict) => {
      const scheduleInfo = resolvedDates[conflict.processId];
      if (!scheduleInfo) return;

      // Find next available slot
      const nextAvailable = findNextAvailableDate(
        conflict.date,
        scheduleInfo.hours,
        calendarData
      );

      if (nextAvailable.availability.available) {
        resolvedDates[conflict.processId] = {
          ...scheduleInfo,
          date: nextAvailable.date,
        };
        console.log(
          `📅 Moved process ${
            conflict.processId
          } to ${nextAvailable.date.toDateString()}`
        );
      }
    });

    attempts++;
  }

  if (attempts >= maxRetries) {
    console.warn(
      `⚠️ Could not resolve all conflicts after ${maxRetries} attempts`
    );
  }

  return resolvedDates;
};

// Draggable Task Component
const DraggableTask = memo(
  ({ task, calendarExpanded, handleDuplicateTask }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
      type: ItemTypes.TASK,
      item: () => {
        console.log("Started dragging calendar task:", task);
        return {
          type: ItemTypes.TASK,
          task: {
            ...task,
            id: task.id,
            scheduledDate: task.scheduledDate,
          },
        };
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }));

    return (
      <div
        key={task.id}
        ref={drag}
        className={`processBox ${!calendarExpanded ? "compact" : ""} ${
          isDragging ? "dragging" : ""
        }`}
        style={{
          borderLeftColor: "#ff9800",
          opacity: isDragging ? 0.5 : 1,
          cursor: "move",
        }}
      >
        <div className="processHeader">
          <div className="processTitle">{task.title || "Task"}</div>
          {calendarExpanded && (
            <div className="processActions">
              <Tooltip title="Duplicate Task">
                <IconButton
                  size="small"
                  onClick={() => handleDuplicateTask(task)}
                  className="actionButton"
                >
                  <DuplicateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
        {calendarExpanded && (
          <div className="processDetails">
            <div>{task.description}</div>
          </div>
        )}
      </div>
    );
  }
);

// Draggable Event Component
const DraggableEvent = memo(
  ({ event, calendarExpanded, handleDuplicateEvent }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
      type: ItemTypes.EVENT,
      item: () => {
        console.log("Started dragging calendar event:", event);
        return {
          type: ItemTypes.EVENT,
          event: {
            ...event,
            id: event.id,
            date: event.date || event.scheduledDate,
          },
        };
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }));

    return (
      <div
        key={event.id}
        ref={drag}
        className={`eventBox ${!calendarExpanded ? "compact" : ""} ${
          isDragging ? "dragging" : ""
        }`}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: "move",
        }}
      >
        <div className="processHeader">
          <div className="processTitle">{event.title || "Event"}</div>
          {calendarExpanded && (
            <div className="processActions">
              <Tooltip title="Duplicate Event">
                <IconButton
                  size="small"
                  onClick={() => handleDuplicateEvent(event)}
                  className="actionButton"
                >
                  <DuplicateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
        {calendarExpanded && (
          <div className="processDetails">
            <div>{event.description}</div>
          </div>
        )}
      </div>
    );
  }
);

const isHolidayDate = (date) => {
  // German holidays example - customize as needed
  const holidays = [
    // Fixed holidays
    { month: 0, day: 1 }, // New Year
    { month: 4, day: 1 }, // Labor Day
    { month: 9, day: 3 }, // German Unity Day
    { month: 11, day: 25 }, // Christmas Day
    { month: 11, day: 26 }, // Boxing Day
  ];

  return holidays.some(
    (holiday) =>
      date.getMonth() === holiday.month && date.getDate() === holiday.day
  );
};
const isWorkingDay = (date) => {
  const day = date.getDay();
  const isWeekend = day === 0 || day === 6; // Sunday or Saturday

  // TODO: Add your specific holiday logic here
  const isHoliday = isHolidayDate(date);

  return !isWeekend && !isHoliday;
};
// Enhanced weekend detection with holiday support
const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday or Saturday (Sonntag oder Samstag)
};

// TODO: Add holiday detection if needed
const isHoliday = (date) => {
  // Placeholder for holiday logic - can be extended with actual holiday data
  return false;
};

// Comprehensive non-working day detection
const isNonWorkingDay = (date) => {
  return isWeekend(date) || isHoliday(date);
};

// Robust working day navigation with proper boundary handling
const getNextWorkingDay = (startDate, direction = 1) => {
  const result = new Date(startDate);
  let attempts = 0;
  const maxAttempts = 20; // Safety limit (covers 4 weeks)

  // Move to next day first
  result.setDate(result.getDate() + direction);

  while (!isWorkingDay(result) && attempts < maxAttempts) {
    result.setDate(result.getDate() + direction);
    attempts++;
  }

  if (attempts >= maxAttempts) {
    console.error("Max attempts reached finding working day from:", startDate);
    // Fallback: return the attempted date anyway
  }

  return result;
};

// Ensure we always get a working day with fallback
const ensureWorkingDay = (date, direction = 1) => {
  if (isNonWorkingDay(date)) {
    return getNextWorkingDay(date, direction);
  }
  return new Date(date);
};

// Calculate working days between two dates
const getWorkingDaysBetween = (startDate, endDate) => {
  if (startDate >= endDate) return 0;
  let count = 1;
  let current = new Date(startDate);
  const end = new Date(endDate);
  let safety = 0;
  const maxSafety = 1000; // Safety for very large date ranges

  current.setDate(current.getDate() + 1); // Start from day after startDate

  while (current < end && safety < maxSafety) {
    if (isWorkingDay(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
    safety++;
  }

  return count;
};
// Add working days to a date (skipping weekends/holidays)
const addWorkingDays = (startDate, daysToAdd = 0, occupiedDate = []) => {
  const result = new Date(startDate);

  const direction = daysToAdd >= 0 ? 1 : -1;
  while (daysToAdd != 0) {
    if (isWorkingDay(result)) {
      daysToAdd -= direction;
    }
    result.setDate(result.getDate() + direction);
  }

  while (
    !isWorkingDay(result) ||
    occupiedDate.includes(getISODateString(result))
  ) {
    result.setDate(result.getDate() + direction);
  }

  return result;
};

const isDateAvailable = (
  date,
  requiredHours,
  calendarData,
  excludeProcessIds = []
) => {
  const dateKey = getISODateString(date); // date.toISOString().split("T")[0];
  const dayData = calendarData[dateKey];

  if (!dayData || !dayData.processes) {
    return {
      available: true,
      conflictingProcesses: [],
      availableHours: 8,
      usedHours: 0,
    };
  }

  // Calculate used hours (excluding specified processes)
  const usedHours = dayData.processes
    .filter((p) => !excludeProcessIds.includes(p._id || p.id))
    .reduce((total, p) => total + (p.hours || 0), 0);

  const availableHours = Math.max(0, 8 - usedHours);
  const available = availableHours >= requiredHours;

  const conflictingProcesses = available
    ? []
    : dayData.processes.filter(
        (p) => !excludeProcessIds.includes(p._id || p.id)
      );

  return {
    available,
    conflictingProcesses,
    availableHours,
    usedHours,
  };
};

// Find next available date with required hours
const findNextAvailableDate = (
  startDate,
  requiredHours,
  calendarData,
  excludeProcessIds = [],
  maxSearchDays = 30
) => {
  let currentDate = new Date(startDate);
  let attempts = 0;

  // Ensure we start with a working day
  if (!isWorkingDay(currentDate)) {
    currentDate = getNextWorkingDay(currentDate, 1);
  }

  while (attempts < maxSearchDays) {
    const availability = isDateAvailable(
      currentDate,
      requiredHours,
      calendarData,
      excludeProcessIds
    );

    if (availability.available) {
      return {
        date: new Date(currentDate),
        availability,
      };
    }

    currentDate = getNextWorkingDay(currentDate, 1);
    attempts++;
  }

  console.warn(`Could not find available date within ${maxSearchDays} days`);
  return {
    date: new Date(startDate),
    availability: {
      available: false,
      conflictingProcesses: [],
      availableHours: 0,
      usedHours: 8,
    },
  };
};

// TaskBox Component
const TaskBox = ({ task, onDuplicate, source }) => {
  // Enhanced drag item for tasks
  const dragItem = {
    type: ItemTypes.TASK,
    task: {
      ...task,
      type: ItemTypes.TASK,
    },
    source,
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.TASK,
      item: dragItem,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [task, source]
  );

  return (
    <StyledCard
      ref={drag}
      color="#ff9800"
      sx={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <CardContent sx={{ padding: 1, "&:last-child": { pb: 1 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" fontWeight="bold">
            {task.description}
          </Typography>
          <Tooltip title="Duplicate Task">
            <IconButton size="small" onClick={() => onDuplicate(task)}>
              <DuplicateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center">
                <PeopleIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="caption">
                  {task.assignee || "Not Assigned"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center">
                <Tooltip title={task.paid ? "Paid" : "Unpaid"}>
                  <Chip
                    label={task.paid ? "Paid" : "Unpaid"}
                    size="small"
                    color={task.paid ? "success" : "default"}
                    variant="outlined"
                  />
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 1 }}
          >
            <Chip
              label={`${task.hours}h`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {task.anr && (
              <Chip label={task.anr} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

// EventBox Component
const EventBox = ({ event, onDuplicate, source }) => {
  // Enhanced drag item for events
  const dragItem = {
    type: ItemTypes.EVENT,
    event: {
      ...event,
      type: ItemTypes.EVENT,
    },
    source,
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.EVENT,
      item: dragItem,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [event, source]
  );

  return (
    <StyledCard
      ref={drag}
      color="#d32f2f"
      sx={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <CardContent sx={{ padding: 1, "&:last-child": { pb: 1 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" fontWeight="bold">
            {event.description}
          </Typography>
          <Tooltip title="Duplicate Event">
            <IconButton size="small" onClick={() => onDuplicate(event)}>
              <DuplicateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Box display="flex" alignItems="center">
            <EventIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption">
              {event.date
                ? new Date(event.date).toLocaleDateString()
                : "Not Scheduled"}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

const DraggableProcessItem = ({ process, draggable }) => {
  // Prepare the drag item only if draggable
  const dragItem = {
    type: ItemTypes.PROCESS,
    process: {
      ...process,
      type: ItemTypes.PROCESS,
      chainId: process.chainId || null,
      scheduledDate: process.scheduledDate || null,
    },
  };

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PROCESS,
    item: () => dragItem,
    canDrag: () => draggable, // Prevent drag if not allowed
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dropResult) {
        console.log("Process was dropped successfully:", dropResult);
      } else {
        console.log("Drop was not successful or was cancelled");
      }
    },
  });

  const content = (
    <Box
      sx={{
        mb: 1,
        opacity: isDragging || !draggable ? 0.5 : 1,
        cursor: draggable ? "grab" : "not-allowed",
        borderRadius: 1,
        p: 0.5,
        backgroundColor: isDragging ? "rgba(0, 0, 0, 0.05)" : "inherit",
        "&:hover": {
          bgcolor: "rgba(0, 0, 0, 0.05)",
        },
        pointerEvents: draggable ? "auto" : "none", // prevent drag but allow tooltip via wrapper
      }}
      ref={draggable ? drag : null}
    >
      <Chip
        sx={{
          mr: 1,
          backgroundColor: processColors[process.id] || "#1976d2",
          color: "white",
        }}
        size="small"
        label={process.id}
      />
      <Typography variant="body2" component="span">
        {process.label} ({process.hours * 60}M)
      </Typography>
    </Box>
  );

  return draggable ? (
    content
  ) : (
    <Tooltip
      title="Weisen Sie zuerst ANR zu, um diesen Prozess ziehbar zu machen"
      arrow
    >
      <span style={{ display: "block" }}>{content}</span>
    </Tooltip>
  );
};
// Enhanced Template component with draggable processes
const Template = ({
  template,
  expanded,
  toggleExpand,
  assignANR,
  isSelected,
  onDeleteTemplate,
  onCopyTemplate,
}) => {
  return (
    <TemplateCard selected={isSelected}>
      <CardHeader
        title={<Typography variant="h6">{template.name}</Typography>}
        action={
          <Box>
            <Tooltip title="Copy Template">
              <IconButton onClick={() => onCopyTemplate(template)}>
                <DuplicateIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Template">
              <IconButton
                onClick={() => onDeleteTemplate(template._id)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? "Hide Processes" : "Show Processes"}>
              <IconButton onClick={toggleExpand}>
                {expanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {template.assignedANR ? (
            <Box>
              <Chip
                icon={<AssignmentIcon />}
                label={template.assignedANR.anr}
                color="primary"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="textSecondary">
                {template.assignedANR.auftragsbezeichnung}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Keine ANR zugewiesen
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={assignANR}
            startIcon={<AssignmentIcon />}
          >
            ANR zuweisen
          </Button>
        </Box>

        {expanded && template.processes && template.processes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Prozesse ({template.processes.length})
            </Typography>
            {template.assignedANR && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Ziehen Sie Prozesse in den Kalender, um sie zu planen
              </Typography>
            )}
            <Box sx={{ maxHeight: "300px", overflowY: "auto", mt: 1 }}>
              {template.processes.map((process) => (
                <DraggableProcessItem
                  key={process._id || process.id}
                  process={process}
                  draggable={Boolean(template.assignedANR)}
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </TemplateCard>
  );
};

const ExpandedTemplatesProcesses = ({
  templates,
  expandedTemplates,
  updateProcessEmployee,
  updateProcessMachine,
  toggleChainView,
  brokenChains,
  toggleChainBroken,
  handleDuplicateProcess,
  employees,
  machines,
  openMengeConfigForProcess,
  projectMengeData,
  onApplyProcessTime,
}) => {
  const [selectedProcesses, setSelectedProcesses] = useState(new Set());
  const [bulkMengeModalOpen, setBulkMengeModalOpen] = useState(false);
  const [processTimeDistribution, setProcessTimeDistribution] = useState({});
  const [sortBy, setSortBy] = useState(""); // name, time, complexity
  const [filterBy, setFilterBy] = useState("all"); // all, configured, unconfigured
  const [viewMode, setViewMode] = useState("grid"); // grid, list, timeline

  const expandedProcesses = useMemo(() => {
    return templates
      .filter((template) => expandedTemplates[template._id])
      .flatMap((template) =>
        (template.processes || []).map((process) => ({
          ...process,
          templateName: template.name,
          templateid: template._id,
          // now template is correctly the one you’re iterating over
          estimatedDays: Math.ceil((process.hours || 0) / 8),
          complexity:
            process.hours > 16 ? "high" : process.hours > 8 ? "medium" : "low",
          mengeConfigured: Boolean(
            processTimeDistribution[`${process.id}-${process.label}`]
          ),
          urgency: process.anr ? "assigned" : "pending",
        }))
      );
  }, [templates, expandedTemplates, processTimeDistribution]);

  // Filter and sort processes
  const filteredProcesses = useMemo(() => {
    let filtered = expandedProcesses;

    // Apply filters
    switch (filterBy) {
      case "configured":
        filtered = filtered.filter((p) => p.mengeConfigured);
        break;
      case "unconfigured":
        filtered = filtered.filter((p) => !p.mengeConfigured);
        break;
      case "assigned":
        filtered = filtered.filter((p) => p.anr);
        break;
      case "unassigned":
        filtered = filtered.filter((p) => !p.anr);
        break;
    }

    // Apply sorting
    switch (sortBy) {
      case "time":
        filtered.sort((a, b) => (b.hours || 0) - (a.hours || 0));
        break;
      case "complexity":
        const complexityOrder = { high: 3, medium: 2, low: 1 };
        filtered.sort(
          (a, b) =>
            complexityOrder[b.complexity] - complexityOrder[a.complexity]
        );
        break;
      case "urgency":
        filtered.sort((a, b) => {
          if (a.urgency === "assigned" && b.urgency === "pending") return -1;
          if (a.urgency === "pending" && b.urgency === "assigned") return 1;
          return 0;
        });
        break;

      case "name":
        filtered.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        break;

      // default: none (as it is)
    }

    return filtered;
  }, [expandedProcesses, sortBy, filterBy]);

  // Calculate aggregate statistics
  const statistics = useMemo(() => {
    const totalHours = expandedProcesses.reduce(
      (sum, p) => sum + (p.hours || 0),
      0
    );
    const configuredCount = expandedProcesses.filter(
      (p) => p.mengeConfigured
    ).length;
    const assignedCount = expandedProcesses.filter((p) => p.anr).length;
    const avgHours =
      expandedProcesses.length > 0 ? totalHours / expandedProcesses.length : 0;

    return {
      total: expandedProcesses.length,
      totalHours,
      avgHours,
      configuredCount,
      assignedCount,
      estimatedDays: Math.ceil(totalHours / 8),
      configurationProgress:
        expandedProcesses.length > 0
          ? (configuredCount / expandedProcesses.length) * 100
          : 0,
    };
  }, [expandedProcesses]);

  // Enhanced Menge-Konfiguration with multi-day support
  const handleQuickMengeConfig = async (
    process,
    mengePerHour,
    workMode = "full"
  ) => {
    if (!projectMengeData) {
      toast.warning("Please select an ANR first to configure Menge");
      return;
    }

    const hoursPerMenge = 1 / mengePerHour; // Convert Menge per hour to Stunden pro Menge
    const dailyCapacity = workMode === "full" ? 8 : workMode === "half" ? 4 : 2; // quarter day
    const mengePerDay = Math.floor(dailyCapacity / hoursPerMenge);

    const totalMengeForProcess = projectMengeData.remainingMenge;
    const totalHoursRequired = totalMengeForProcess * hoursPerMenge;
    const daysRequired = Math.ceil(totalMengeForProcess / mengePerDay);

    // Create multi-day breakdown if exceeds 8 hours
    const processInstances = [];
    let remainingMenge = totalMengeForProcess;

    for (let day = 1; day <= daysRequired && remainingMenge > 0; day++) {
      const dayMenge = Math.min(mengePerDay, remainingMenge);
      const dayHours = dayMenge * hoursPerMenge;

      const instanceId = `${process.id}-d${day}`;

      const processInstance = {
        ...process,
        instanceId,
        originalProcessId: process.id,
        label:
          daysRequired > 1
            ? `${process.label} (Day ${day}/${daysRequired})`
            : process.label,
        hours: Math.min(dayHours, dailyCapacity),
        hoursForThisDay: Math.min(dayHours, dailyCapacity),
        dayNumber: day,
        totalDays: daysRequired,
        isMultiDay: daysRequired > 1,
        isInstance: day > 1,
        instanceNumber: day,

        // Menge-specific data
        mengeForDay: dayMenge,
        totalMengeForProcess,
        hoursPerMenge,
        mengePerDay,
        workMode,

        // Visual indicators
        color: day === 1 ? process.color : `${process.color}80`, // Slightly transparent for subsequent days
        dayFraction: Math.min(dayHours / 8, 1),
        efficiency: (dayMenge / mengePerDay) * 100,
      };

      processInstances.push(processInstance);
      remainingMenge -= dayMenge;
    }

    // Store the configuration for reference
    const configKey = `${process.id}-${process.label}`;
    setProcessTimeDistribution((prev) => ({
      ...prev,
      [configKey]: {
        hoursPerMenge,
        mengePerDay,
        workMode,
        daysRequired,
        totalHours: totalHoursRequired,
        instances: processInstances,
        configuredAt: new Date(),
      },
    }));

    toast.success(
      `${process.label} configured: ${daysRequired} day${
        daysRequired > 1 ? "s" : ""
      }, ${totalMengeForProcess} units`
    );
  };

  // Bulk operations for selected processes
  const handleBulkMengeConfig = () => {
    if (selectedProcesses.size === 0) {
      toast.warning("Please select processes to configure");
      return;
    }
    setBulkMengeModalOpen(true);
  };

  const toggleProcessSelection = (processId) => {
    setSelectedProcesses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(processId)) {
        newSet.delete(processId);
      } else {
        newSet.add(processId);
      }
      return newSet;
    });
  };

  if (expandedProcesses.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 4, mt: 3, mb: 3, textAlign: "center" }}>
        <ConstructionIcon
          sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Keine Vorlagenprozesse erweitert
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Erweitern Sie die Vorlagen oben, um Prozesse mit Menge-Management
          anzuzeigen und zu konfigurieren
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 0, mt: 3, mb: 3, overflow: "hidden" }}>
      {/* Professional Header with Statistics Dashboard */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "white",
          p: 3,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              <BuildIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Produktionsprozessmanagement
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Professional Menge-based Zeitverteilung and scheduling
            </Typography>
          </Box>

          {projectMengeData && (
            <Card
              sx={{
                minWidth: 200,
                bgcolor: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  PROJECT MENGE
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {projectMengeData.remainingMenge.toFixed(0)} /{" "}
                  {projectMengeData.totalMenge}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  {projectMengeData.percentDone.toFixed(1)}% Complete • ANR:{" "}
                  {projectMengeData.anr}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Statistics Row */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold">
                {statistics.total}
              </Typography>
              <Typography variant="caption">Gesamtprozesse</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold">
                {statistics.totalHours.toFixed(0) * 60}m
              </Typography>
              <Typography variant="caption">Gesamtminuten</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold">
                {statistics.estimatedDays}
              </Typography>
              <Typography variant="caption">Est. Tage</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold">
                {statistics.configurationProgress.toFixed(0)}%
              </Typography>
              <Typography variant="caption">Konfiguriert</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Professional Control Panel */}
      <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0", bgcolor: "#f8f9fa" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box display="flex" gap={1} flexWrap="wrap">
              {/* View Mode Toggle */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Ansichtsmodus</InputLabel>
                <Select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                >
                  <MenuItem value="grid">Rasteransicht</MenuItem>
                  <MenuItem value="list">Listenansicht</MenuItem>
                  <MenuItem value="timeline">Zeitleiste</MenuItem>
                </Select>
              </FormControl>

              {/* Sort Options */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sortieren nach</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="time">Dauer</MenuItem>
                  <MenuItem value="complexity">Komplexität</MenuItem>
                  <MenuItem value="urgency">Dringlichkeit</MenuItem>
                </Select>
              </FormControl>

              {/* Filter Options */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                >
                  <MenuItem value="all">Alle Prozesse</MenuItem>
                  <MenuItem value="configured">Konfiguriert</MenuItem>
                  <MenuItem value="unconfigured">Unkonfiguriert</MenuItem>
                  <MenuItem value="assigned">ANR zugewiesen</MenuItem>
                  <MenuItem value="unassigned">Kein ANR</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              display="flex"
              justifyContent="flex-end"
              gap={1}
              flexWrap="wrap"
            >
              {selectedProcesses.size > 0 && (
                <Chip
                  label={`${selectedProcesses.size} selected`}
                  color="primary"
                  onDelete={() => setSelectedProcesses(new Set())}
                />
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={handleBulkMengeConfig}
                disabled={selectedProcesses.size === 0}
                startIcon={<SettingsIcon />}
              >
                Massenkonfiguration
              </Button>

              {/* {projectMengeData && (
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  startIcon={<EmojiEventsIcon />}
                >
                  Auto-Optimize All
                </Button>
              )} */}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ p: 3 }}>
        {viewMode === "grid" && (
          <Grid container spacing={2}>
            {filteredProcesses.map((process) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                lg={4}
                key={process._id || process.id}
              >
                <EnhancedProcessCard
                  templateId={process.templateid}
                  process={process}
                  employees={employees}
                  machines={machines}
                  updateProcessEmployee={updateProcessEmployee}
                  updateProcessMachine={updateProcessMachine}
                  toggleChainView={toggleChainView}
                  isChainBroken={brokenChains[process.chainId]}
                  toggleChainBroken={toggleChainBroken}
                  onDuplicate={() => handleDuplicateProcess(process)}
                  openMengeConfigForProcess={openMengeConfigForProcess}
                  projectMengeData={projectMengeData}
                  onQuickMengeConfig={handleQuickMengeConfig}
                  isSelected={selectedProcesses.has(process.id)}
                  onToggleSelection={() => toggleProcessSelection(process.id)}
                  timeDistribution={
                    processTimeDistribution[`${process.id}-${process.label}`]
                  }
                  onApplyProcessTime={onApplyProcessTime}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {viewMode === "list" && (
          <Box>
            {filteredProcesses.map((process, index) => (
              <EnhancedProcessListItem
                key={process._id || process.id}
                process={process}
                index={index}
                employees={employees}
                machines={machines}
                updateProcessEmployee={updateProcessEmployee}
                updateProcessMachine={updateProcessMachine}
                openMengeConfigForProcess={openMengeConfigForProcess}
                projectMengeData={projectMengeData}
                onQuickMengeConfig={handleQuickMengeConfig}
                isSelected={selectedProcesses.has(process.id)}
                onToggleSelection={() => toggleProcessSelection(process.id)}
                timeDistribution={
                  processTimeDistribution[`${process.id}-${process.label}`]
                }
              />
            ))}
          </Box>
        )}

        {viewMode === "timeline" && (
          <ProcessTimelineView
            processes={filteredProcesses}
            timeDistribution={processTimeDistribution}
            projectMengeData={projectMengeData}
          />
        )}
      </Box>

      {/* Bulk Menge-Konfiguration Modal */}
      <BulkMengeConfigModal
        open={bulkMengeModalOpen}
        onClose={() => setBulkMengeModalOpen(false)}
        selectedProcesses={filteredProcesses.filter((p) =>
          selectedProcesses.has(p.id)
        )}
        projectMengeData={projectMengeData}
        onApplyConfig={(config) => {
          selectedProcesses.forEach((processId) => {
            const process = filteredProcesses.find((p) => p.id === processId);
            if (process) {
              handleQuickMengeConfig(
                process,
                config.mengePerHour,
                config.workMode
              );
            }
          });
          setBulkMengeModalOpen(false);
          setSelectedProcesses(new Set());
        }}
      />
    </Paper>
  );
};

const EnhancedProcessCard = ({
  templateId,
  process,
  employees,
  machines,
  updateProcessEmployee,
  updateProcessMachine,
  isChainBroken,
  toggleChainBroken,
  onDuplicate,
  openMengeConfigForProcess,
  projectMengeData,
  onQuickMengeConfig,
  isSelected,
  onToggleSelection,
  timeDistribution,
  onApplyProcessTime,
}) => {
  const [quickConfigOpen, setQuickConfigOpen] = useState(false);
  const [localProcessTime, setLocalProcessTime] = useState({
    startupTime: 0,
    processingTime: 480,
  });

  // Add drag functionality
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PROCESS,
    item: () => ({
      type: ItemTypes.PROCESS,
      process: {
        ...process,
        source: "template",
        templateId: templateId,
      },
    }),
    canDrag: () => Boolean(process.anr), // Only draggable if ANR is assigned
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case "high":
        return "#d32f2f";
      case "medium":
        return "#f57c00";
      case "low":
        return "#388e3c";
      default:
        return "#1976d2";
    }
  };

  const getStatusColor = (status) => {
    if (timeDistribution) return "#4caf50"; // Configured
    if (process.anr) return "#2196f3"; // Assigned
    return "#757575"; // Unassigned
  };

  return (
    <Card
      ref={drag}
      elevation={isSelected ? 8 : 2}
      sx={{
        position: "relative",
        transition: "transform 0.2s ease, box-shadow 0.3s ease",
        transform: isSelected ? "translateY(-4px)" : "none",
        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
        },
        borderRadius: 2,
        opacity: isDragging ? 0.5 : 1,
        cursor: process.anr ? "grab" : "default",
      }}
    >
      {/* Selection Checkbox */}
      <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 2 }}>
        <Tooltip title={isSelected ? "Deselect process" : "Select process"}>
          <IconButton
            size="small"
            onClick={onToggleSelection}
            sx={{
              bgcolor: isSelected ? "primary.main" : "rgba(0,0,0,0.08)",
              color: isSelected ? "white" : "text.secondary",
              "&:hover": {
                bgcolor: isSelected ? "primary.dark" : "rgba(0,0,0,0.15)",
              },
            }}
          >
            {isSelected ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status Indicator Ribbon */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 0,
          height: 0,
          borderStyle: "solid",
          borderWidth: "0 40px 40px 0",
          borderColor: `transparent ${getStatusColor()} transparent transparent`,
        }}
      />

      {/* Header */}
      <CardHeader
        title={
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {process.label}
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
              <Chip
                label={process.complexity.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: getComplexityColor(process.complexity),
                  color: "white",
                  fontSize: "10px",
                  height: 20,
                }}
              />
              <Chip
                label={`${process.hours || 0}h`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: "10px", height: 20 }}
              />
              {process.anr ? (
                <>
                  <Chip
                    label={process.anr}
                    size="small"
                    color="secondary"
                    sx={{ fontSize: "10px", height: 20 }}
                  />
                  <Tooltip title="Drag this process to the calendar">
                    <Chip
                      label="🖱️ DRAGGABLE"
                      size="small"
                      color="success"
                      sx={{ fontSize: "10px", height: 20, fontWeight: 600 }}
                    />
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Assign ANR first to make this process draggable">
                  <Chip
                    label="⚠️ ASSIGN ANR"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ fontSize: "10px", height: 20 }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        }
        sx={{ pb: 1, pt: 5 }}
      />

      {/* Content */}
      <CardContent sx={{ pt: 0 }}>
        {/* Employee Select */}
        <Box mb={2} mt={1}>
          <FormControl fullWidth size="small">
            <InputLabel>Zugewiesener Mitarbeiter</InputLabel>
            <Select
              value={process.names?.[0] || ""}
              onChange={(e) =>
                updateProcessEmployee(templateId, process.id, e.target.value)
              }
              label="Zugewiesener Mitarbeiter"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee._id} value={employee.name}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: "10px" }}>
                      {employee.name[0]}
                    </Avatar>
                    {employee.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Machine Select */}
        <Box mb={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Machine</InputLabel>
            <Select
              value={process.machinename}
              onChange={(e) =>
                updateProcessMachine(templateId, process.id, e.target.value)
              }
              label="Machine"
            >
              <MenuItem value="">
                <em>No Machine</em>
              </MenuItem>
              {machines.map((machine) => (
                <MenuItem key={machine._id} value={machine.prozess}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BuildIcon fontSize="small" />
                    {machine.prozess}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {process.machinename && (
          <Box mb={2} display="flex" gap={1} alignItems="center">
            <TextField
              label="Startzeit (min)"
              type="number"
              size="small"
              value={localProcessTime.startupTime}
              onChange={(e) => {
                const value = e.target.value;
                setLocalProcessTime((prev) => ({
                  ...prev,
                  startupTime: value ? parseInt(value, 10) : 0,
                }));
              }}
              sx={{ flex: 1 }}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => onApplyProcessTime(process, localProcessTime)}
            >
              Anwenden
            </Button>
          </Box>
        )}

        {process.machinename && (
          <Box mb={2} display="flex" gap={1} alignItems="center">
            <TextField
              label="Process Time (min)"
              type="number"
              size="small"
              value={localProcessTime.processingTime}
              onChange={(e) => {
                const value = e.target.value;
                setLocalProcessTime((prev) => ({
                  ...prev,
                  processingTime: value ? parseInt(value, 10) : 0,
                }));
              }}
              sx={{ flex: 1 }}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => onApplyProcessTime(process, localProcessTime)}
            >
              Anwenden
            </Button>
          </Box>
        )}

        {/* Menge Config Status */}
        {timeDistribution ? (
          <Box mb={2}>
            <Alert severity="success" sx={{ p: 1 }}>
              <Typography variant="caption">
                <strong>Configured:</strong> {timeDistribution.daysRequired} day
                {timeDistribution.daysRequired > 1 ? "s" : ""},{" "}
                {timeDistribution.mengePerDay} units/day (
                {timeDistribution.workMode})
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Total: {timeDistribution.totalHours.toFixed(1)}h • Configured:{" "}
                {new Date(timeDistribution.configuredAt).toLocaleString()}
              </Typography>
            </Alert>
          </Box>
        ) : projectMengeData ? (
          <Box mb={2}>
            <Alert severity="info" sx={{ p: 1 }}>
              <Typography variant="caption">
                Menge not configured. Click "Quick Config" to set time
                distribution.
              </Typography>
            </Alert>
          </Box>
        ) : null}
      </CardContent>

      {/* Action Buttons */}
      <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
        <Box display="flex" gap={0.5}>
          {/* Quick Config */}
          {projectMengeData && (
            <Tooltip title="Quick Menge-Konfiguration">
              <IconButton
                size="small"
                onClick={() => setQuickConfigOpen(true)}
                color="secondary"
                sx={{ bgcolor: "rgba(156, 39, 176, 0.1)" }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Advanced Config */}
          <Tooltip title="Advanced Menge-Konfiguration">
            <IconButton
              size="small"
              onClick={() => openMengeConfigForProcess(process)}
              color="primary"
              disabled={!projectMengeData}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Duplicate */}
          <Tooltip title="Duplicate Process">
            <IconButton size="small" onClick={onDuplicate} color="info">
              <DuplicateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Chain Toggle */}
        <Tooltip title={isChainBroken ? "Repair Chain" : "Break Chain"}>
          <IconButton
            size="small"
            onClick={() => toggleChainBroken(process.chainId)}
            color={isChainBroken ? "error" : "primary"}
          >
            {isChainBroken ? (
              <BrokenChainIcon fontSize="small" />
            ) : (
              <ChainIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </CardActions>

      {/* Quick Config Popover */}
      <Menu
        open={quickConfigOpen}
        onClose={() => setQuickConfigOpen(false)}
        anchorEl={null}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        sx={{ mt: 1 }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            Quick Menge-Konfiguration
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {[
            {
              label: "Slow Process (2h/unit, Full Day)",
              config: [0.5, "full"],
            },
            {
              label: "Standard Process (1h/unit, Full Day)",
              config: [1, "full"],
            },
            {
              label: "Fast Process (0.5h/unit, Half Day)",
              config: [2, "half"],
            },
          ].map(({ label, config }, i) => (
            <Button
              key={i}
              fullWidth
              variant="outlined"
              onClick={() => {
                onQuickMengeConfig(process, ...config);
                setQuickConfigOpen(false);
              }}
              sx={{ mb: 1 }}
            >
              {label}
            </Button>
          ))}

          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              openMengeConfigForProcess(process);
              setQuickConfigOpen(false);
            }}
          >
            Custom Configuration
          </Button>
        </Box>
      </Menu>
    </Card>
  );
};

const EnhancedProcessListItem = ({
  process,
  index,
  employees,
  machines,
  updateProcessEmployee,
  updateProcessMachine,
  openMengeConfigForProcess,
  projectMengeData,
  onQuickMengeConfig,
  isSelected,
  onToggleSelection,
  timeDistribution,
}) => {
  // Add drag functionality
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PROCESS,
    item: () => ({
      type: ItemTypes.PROCESS,
      process: {
        ...process,
        source: "template",
        templateId: process.templateid,
      },
    }),
    canDrag: () => Boolean(process.anr), // Only draggable if ANR is assigned
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  return (
    <Card
      ref={drag}
      elevation={isSelected ? 4 : 1}
      sx={{
        mb: 1,
        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
        opacity: isDragging ? 0.5 : 1,
        cursor: process.anr ? "grab" : "default",
        "&:hover": { elevation: 3 },
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={1}>
            <IconButton size="small" onClick={onToggleSelection}>
              {isSelected ? <CheckCircleIcon color="primary" /> : <AddIcon />}
            </IconButton>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle1" fontWeight="bold">
              {process.label}
            </Typography>
            <Box display="flex" gap={0.5} mt={0.5}>
              <Chip
                label={`${process.hours}h`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={process.complexity}
                size="small"
                color="secondary"
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={2}>
            <FormControl fullWidth size="small">
              <Select
                value={process.names?.[0] || ""}
                onChange={(e) =>
                  updateProcessEmployee(process.id, e.target.value)
                }
                displayEmpty
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee._id} value={employee.name}>
                    {employee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={2}>
            <FormControl fullWidth size="small">
              <Select
                value={process.machinename || ""}
                onChange={(e) =>
                  updateProcessMachine(process.id, e.target.value)
                }
                displayEmpty
              >
                <MenuItem value="">
                  <em>No Machine</em>
                </MenuItem>
                {machines.map((machine) => (
                  <MenuItem key={machine._id} value={machine.name}>
                    {machine.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={2}>
            {timeDistribution ? (
              <Chip
                label={`${timeDistribution.daysRequired}d configured`}
                color="success"
                size="small"
              />
            ) : (
              <Chip
                label="Not configured"
                color="warning"
                variant="outlined"
                size="small"
              />
            )}
          </Grid>

          <Grid item xs={2}>
            <Box display="flex" gap={0.5}>
              <Tooltip title="Quick Config">
                <IconButton
                  size="small"
                  onClick={() => onQuickMengeConfig(process, 1, "full")}
                  disabled={!projectMengeData}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Advanced Config">
                <IconButton
                  size="small"
                  onClick={() => openMengeConfigForProcess(process)}
                  disabled={!projectMengeData}
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// === PROCESS TIMELINE VIEW COMPONENT ===
const ProcessTimelineView = ({
  processes,
  timeDistribution,
  projectMengeData,
}) => {
  return (
    <Box sx={{ position: "relative", pl: 4 }}>
      {/* Timeline Line */}
      <Box
        sx={{
          position: "absolute",
          left: 20,
          top: 0,
          bottom: 0,
          width: 2,
          bgcolor: "primary.main",
          opacity: 0.3,
        }}
      />

      {processes.map((process, index) => {
        const config = timeDistribution[`${process.id}-${process.label}`];

        return (
          <Box key={process.id} sx={{ position: "relative", mb: 3 }}>
            {/* Timeline Node */}
            <Box
              sx={{
                position: "absolute",
                left: -24,
                top: 20,
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: config ? "success.main" : "warning.main",
                border: "2px solid white",
                boxShadow: 2,
              }}
            />

            <Card elevation={2}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box flex={1}>
                    <Typography variant="h6" gutterBottom>
                      {process.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Duration: {process.hours}h • Complexity:{" "}
                      {process.complexity}
                    </Typography>

                    {config && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" color="success.main">
                          ✓ Configured for {config.daysRequired} day
                          {config.daysRequired > 1 ? "s" : ""}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {config.mengePerDay} units/day • {config.workMode} day
                          schedule
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total hours: {config.totalHours.toFixed(1)}h
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Chip
                      label={config ? "Configured" : "Pending"}
                      color={config ? "success" : "warning"}
                      size="small"
                    />
                    {process.anr && (
                      <Chip
                        label={process.anr}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );
      })}
    </Box>
  );
};

// === BULK Menge-Konfiguration MODAL ===
const BulkMengeConfigModal = ({
  open,
  onClose,
  selectedProcesses,
  projectMengeData,
  onApplyConfig,
}) => {
  const [mengePerHour, setMengePerHour] = useState(1);
  const [workMode, setWorkMode] = useState("full");

  const handleApply = () => {
    onApplyConfig({ mengePerHour, workMode });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Bulk Menge-Konfiguration
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Configure {selectedProcesses.length} selected processes
        </Typography>

        <TextField
          fullWidth
          label="Menge per Hour"
          type="number"
          value={mengePerHour}
          onChange={(e) => setMengePerHour(parseFloat(e.target.value))}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Work Mode</InputLabel>
          <Select
            value={workMode}
            onChange={(e) => setWorkMode(e.target.value)}
          >
            <MenuItem value="full">Full Day (8h)</MenuItem>
            <MenuItem value="half">Half Day (4h)</MenuItem>
            <MenuItem value="quarter">Quarter Day (2h)</MenuItem>
          </Select>
        </FormControl>

        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply}>
            Bewerben Sie sich für alle
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// ANRModal Component
const ANRModal2 = ({ open, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestProject, setLatestProject] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${base_url}/v1/getAllProjects`);
        if (response.data.status === 1) {
          const allProjects = response.data.data;
          if (allProjects.length > 0) {
            const sortedProjects = allProjects.sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            setLatestProject(sortedProjects[0]);
            setProjects(allProjects);
            const filteredResult = filterProject(allProjects);
            setFilteredProjects(filteredResult);
          } else {
            setError("No projects found.");
          }
        } else {
          setError("Failed to retrieve projects.");
        }
      } catch (err) {
        setError("Error while fetching projects.");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const filterProject = (projects) => {
    const filterResult = projects.filter((project) => {
      const matchesANR = project.ANR.toLowerCase().includes(
        searchTerm.toLowerCase()
      );
      const matchesAuftragsbezeichnung = project.Auftragsbezeichnung
        ? project.Auftragsbezeichnung.toLowerCase().includes(
            searchTerm.toLowerCase()
          )
        : false;

      const createdAt = new Date(project.createdAt);
      const createdYear = createdAt.getFullYear().toString();
      const createdMonth = (createdAt.getMonth() + 1)
        .toString()
        .padStart(2, "0");

      const matchesYear = year ? createdYear === year : true;
      const matchesMonth = month ? createdMonth === month : true;

      return (
        (matchesANR || matchesAuftragsbezeichnung) &&
        matchesYear &&
        matchesMonth
      );
    });
    return filterResult;
  };

  useEffect(() => {
    const filtered = filterProject(projects);
    setFilteredProjects(filtered);
  }, [searchTerm, year, month, projects]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="anr-modal-title"
      aria-describedby="anr-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "500px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" id="anr-modal-title" gutterBottom>
          Select ANR
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              label="Search ANR or Auftragsbezeichnung"
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Year</InputLabel>
              <Select
                value={year}
                label="Select Year"
                onChange={(e) => setYear(e.target.value)}
              >
                <MenuItem value="">All Years</MenuItem>
                {[
                  ...new Set(
                    projects.map((project) =>
                      new Date(project.createdAt).getFullYear()
                    )
                  ),
                ].map((yearOption) => (
                  <MenuItem key={yearOption} value={yearOption.toString()}>
                    {yearOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Month</InputLabel>
              <Select
                value={month}
                label="Select Month"
                onChange={(e) => setMonth(e.target.value)}
              >
                <MenuItem value="">All Months</MenuItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(
                  (monthOption) => (
                    <MenuItem
                      key={monthOption}
                      value={monthOption.toString().padStart(2, "0")}
                    >
                      {new Date(0, monthOption - 1).toLocaleString("de-DE", {
                        month: "long",
                      })}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, maxHeight: "300px", overflowY: "auto" }}>
          {loading && (
            <Box display="flex" justifyContent="center" my={3}>
              <Typography>Loading projects...</Typography>
            </Box>
          )}

          {error && (
            <Box display="flex" justifyContent="center" my={3}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {latestProject && (
            <Button
              key={latestProject._id}
              variant="contained"
              fullWidth
              sx={{ mb: 1 }}
              onClick={() => {
                onSelect({
                  anr: latestProject.ANR,
                  auftragsbezeichnung: latestProject.Auftragsbezeichnung,
                  maxLohnaufwand: latestProject.MaxLohnaufwand || null,
                  projectId: latestProject._id,
                });
                onClose();
              }}
              startIcon={<EmojiEventsIcon />}
            >
              Latest Project: {latestProject.ANR}
            </Button>
          )}

          <Grid container spacing={1}>
            {filteredProjects.map((project) => (
              <Grid item xs={12} key={project._id}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    mb: 1,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  onClick={() => {
                    onSelect({
                      anr: project.ANR,
                      auftragsbezeichnung: project.Auftragsbezeichnung,
                      maxLohnaufwand: project.MaxLohnaufwand || null,
                      projectId: project._id,
                    });
                    onClose();
                  }}
                  startIcon={<AssignmentIcon />}
                >
                  <Box>
                    <Typography variant="body2" align="left" fontWeight="bold">
                      {project.ANR}
                    </Typography>
                    <Typography
                      variant="caption"
                      align="left"
                      display="block"
                      noWrap
                    >
                      {project.Auftragsbezeichnung}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

// CopyTemplateModal Component
const CopyTemplateModal = ({ open, onClose, onCopy }) => {
  const [newName, setNewName] = useState("");

  const handleCopy = () => {
    if (newName.trim() === "") {
      toast.error("Bitte geben Sie einen gültigen Namen ein.");
      return;
    }
    onCopy(newName.trim());
    setNewName("");
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        setNewName("");
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "400px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Template kopieren
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <TextField
          variant="outlined"
          margin="normal"
          label="Neuer Template-Name"
          fullWidth
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{ mr: 1 }}
          >
            Abbrechen
          </Button>
          <Button variant="contained" color="primary" onClick={handleCopy}>
            Kopieren
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// TaskModal Component
const TaskModal = ({
  open,
  onClose,
  onCreate,
  employees,
  groups,
  openAnrModalForTask,
  assignedTaskAnrs,
}) => {
  const [taskType, setTaskType] = useState("individual");
  const [description, setDescription] = useState("");
  const [hours, setHours] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedGroup, setSelectedGroup] = useState({});
  const [anr, setAnr] = useState("");
  const [isPaid, setIsPaid] = useState(true);
  const [tempTaskId, setTempTaskId] = useState(null);

  useEffect(() => {
    if (tempTaskId && assignedTaskAnrs[tempTaskId]) {
      setAnr(assignedTaskAnrs[tempTaskId]);
    }
  }, [assignedTaskAnrs, tempTaskId]);

  const handleSelectAnr = () => {
    const newTempTaskId = uuidv4();
    setTempTaskId(newTempTaskId);
    openAnrModalForTask(newTempTaskId);
  };

  const handleSubmit = () => {
    if (!description || !hours) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (taskType === "individual" && !selectedEmployee) {
      toast.error("Please select an employee.");
      return;
    }

    if (taskType === "group" && !selectedGroup.name) {
      toast.error("Please select a group.");
      return;
    }

    const newTask = {
      id: uuidv4(),
      type: ItemTypes.TASK,
      description,
      hours: parseFloat(hours),
      assignee:
        taskType === "individual" ? selectedEmployee : selectedGroup.name,
      assigneeId:
        taskType === "individual" ? selectedEmployee : selectedGroup._id,
      anr: anr || "Not Assigned",
      scheduledDate: null,
      paid: isPaid,
      chainId: null,
    };

    onCreate(newTask);

    // Reset fields
    setTaskType("individual");
    setDescription("");
    setHours("");
    setSelectedEmployee("");
    setSelectedGroup({});
    setAnr("");
    setIsPaid(true);
    setTempTaskId(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        setTempTaskId(null);
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "500px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Neue Aufgabe erstellen
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
          >
            <FormControlLabel
              value="individual"
              control={<Radio />}
              label="Individual"
            />
            <FormControlLabel value="group" control={<Radio />} label="Group" />
          </RadioGroup>
        </FormControl>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Description"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              label="Hours"
              type="number"
              fullWidth
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              InputProps={{ inputProps: { min: 0, step: 0.5 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={isPaid ? "paid" : "unpaid"}
                onChange={(e) => setIsPaid(e.target.value === "paid")}
              >
                <FormControlLabel
                  value="paid"
                  control={<Radio color="success" />}
                  label="Paid"
                />
                <FormControlLabel
                  value="unpaid"
                  control={<Radio color="default" />}
                  label="Unpaid"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleSelectAnr}
              startIcon={<AssignmentIcon />}
            >
              {anr ? `Selected ANR: ${anr}` : "Select ANR"}
            </Button>
          </Grid>

          <Grid item xs={12}>
            {taskType === "individual" ? (
              <FormControl fullWidth>
                <InputLabel>Select Employee</InputLabel>
                <Select
                  value={selectedEmployee}
                  label="Select Employee"
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select Employee</em>
                  </MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee._id} value={employee.name}>
                      {employee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select Group</InputLabel>
                <Select
                  value={selectedGroup._id || ""}
                  label="Select Group"
                  onChange={(e) => {
                    const group = groups.find((g) => g._id === e.target.value);
                    setSelectedGroup(group || {});
                  }}
                >
                  <MenuItem value="">
                    <em>Select Group</em>
                  </MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group._id} value={group._id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Create Task
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// EventModal Component
const EventModal = ({ open, onClose, onCreate }) => {
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(null);

  const handleSubmit = () => {
    if (!description) {
      toast.error("Please fill in the description.");
      return;
    }

    const newEvent = {
      id: uuidv4(),
      type: ItemTypes.EVENT,
      description,
      date: date ? new Date(date) : null,
      chainId: null,
    };

    onCreate(newEvent);

    // Reset fields
    setDescription("");
    setDate(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "500px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Neues Ereignis erstellen
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Description"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              label="Date"
              type="date"
              fullWidth
              value={date || ""}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Create Event
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// === PROFESSIONAL Menge-Konfiguration MODAL ===
// Comprehensive interface for process-specific quantity and time management
const MengeConfigurationModal = ({
  open,
  onClose,
  selectedProcess,
  projectMengeData,
  currentConfig,
  DAY_FRACTIONS,
  onConfigUpdate,
  onApplyToCalendar,
  mengeCalculations,
  selectedDayFraction,
  setSelectedDayFraction,
}) => {
  const [hoursPerMenge, setHoursPerMenge] = useState(1);
  const [mengePerDay, setMengePerDay] = useState(1);
  const [customHours, setCustomHours] = useState(8);
  const [setupTime, setSetupTime] = useState(0);
  const [cleanupTime, setCleanupTime] = useState(0);
  const [efficiency, setEfficiency] = useState(100);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [previewCalculation, setPreviewCalculation] = useState(null);

  // Initialize form with current configuration
  useEffect(() => {
    if (currentConfig && open) {
      setHoursPerMenge(currentConfig.hoursPerMenge || 1);
      setMengePerDay(currentConfig.mengePerDay || 1);
      setSelectedDayFraction(currentConfig.dayFraction || DAY_FRACTIONS.FULL);
      setCustomHours(currentConfig.customHours || 8);
      setSetupTime(currentConfig.setupTime || 0);
      setCleanupTime(currentConfig.cleanupTime || 0);
      setEfficiency(currentConfig.efficiency || 100);
    }
  }, [currentConfig, open]);

  // Real-time calculation preview
  useEffect(() => {
    if (projectMengeData && hoursPerMenge > 0 && mengePerDay > 0) {
      const calculation = calculateMengeTimeDistribution(
        projectMengeData.remainingMenge,
        hoursPerMenge,
        mengePerDay
      );
      setPreviewCalculation(calculation);
    }
  }, [hoursPerMenge, mengePerDay, projectMengeData, selectedDayFraction]);

  const calculateMengeTimeDistribution = (
    totalMenge,
    hoursPerMenge,
    mengePerDay
  ) => {
    const allowedHoursPerDay = selectedDayFraction?.hours || 8;
    const totalHoursRequired = totalMenge * hoursPerMenge;
    const daysByTime = Math.ceil(totalHoursRequired / allowedHoursPerDay);
    const daysByUnits = Math.ceil(totalMenge / mengePerDay);
    const daysRequired = Math.max(daysByTime, daysByUnits);
    const hoursPerDay = totalHoursRequired / daysRequired;

    let optimalFraction = DAY_FRACTIONS.FULL;
    if (hoursPerDay <= 2) optimalFraction = DAY_FRACTIONS.QUARTER;
    else if (hoursPerDay <= 8 / 3) optimalFraction = DAY_FRACTIONS.THIRD;
    else if (hoursPerDay <= 4) optimalFraction = DAY_FRACTIONS.HALF;

    let remainingHours = totalHoursRequired;
    let remainingUnits = totalMenge;

    const breakdown = [];
    let day = 1;

    while (remainingHours > 0) {
      const maxHoursByUnits = mengePerDay * hoursPerMenge;
      const dayHours = Math.min(
        remainingHours,
        allowedHoursPerDay,
        maxHoursByUnits
      );
      const dayMenge = dayHours / hoursPerMenge;

      breakdown.push({
        day,
        menge: dayMenge,
        hours: dayHours,
        fraction: dayHours / allowedHoursPerDay,
      });

      remainingHours -= dayHours;
      remainingUnits -= dayMenge;
      day++;
    }

    return {
      totalMenge,
      hoursPerMenge,
      mengePerDay,
      totalHoursRequired,
      daysRequired,
      hoursPerDay,
      optimalFraction,
      efficiency: Math.min(100, (hoursPerDay / 8) * 100),
      dayBreakdown: breakdown,
    };
  };

  const handleSaveConfiguration = () => {
    const config = {
      processId: selectedProcess?.id,
      processLabel: selectedProcess?.label,
      hoursPerMenge,
      mengePerDay,
      dayFraction: selectedDayFraction,
      customHours: selectedDayFraction.value === 0 ? customHours : null,
      setupTime,
      cleanupTime,
      efficiency,
    };

    onConfigUpdate(selectedProcess?.id, config);
    toast.success("Menge-Konfiguration saved successfully!");
  };

  const handleApplyToCalendar = () => {
    handleSaveConfiguration();
    onApplyToCalendar(selectedProcess, startDate, selectedProcess?.chainId);
  };

  if (!projectMengeData) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "800px", md: "900px" },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          overflow: "auto",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            <ConstructionIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Menge-Konfiguration: {selectedProcess?.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Konfigurieren Sie Mengenproduktionsparameter und Zeitverteilung for
            ANR: {projectMengeData.anr}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Project Menge Overview */}
            <Grid item xs={12}>
              <Card sx={{ mb: 3, bgcolor: "#f8f9fa" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Project Menge Overview
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Menge
                      </Typography>
                      <Typography variant="h6">
                        {projectMengeData.totalMenge}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Übrig
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {projectMengeData.remainingMenge.toFixed(1)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="h6">
                        {projectMengeData.percentDone.toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Cost per Unit
                      </Typography>
                      <Typography variant="h6">
                        €{projectMengeData.singleMengeCost}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Configuration Form */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Zeitkonfiguration
              </Typography>

              <TextField
                fullWidth
                label="Stunden pro Menge"
                type="number"
                value={hoursPerMenge}
                onChange={(e) =>
                  setHoursPerMenge(parseFloat(e.target.value) || 0)
                }
                inputProps={{ min: 0.1, step: 0.1 }}
                sx={{ mb: 2 }}
                helperText="Erforderliche Zeit zum Fertigstellen einer Einheit/Charge"
              />

              <TextField
                fullWidth
                label="Menge per Day"
                type="number"
                value={mengePerDay}
                onChange={(e) => setMengePerDay(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1 }}
                sx={{ mb: 2 }}
                helperText="Number of units/batches completed per day"
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Day Fraction</InputLabel>
                <Select
                  value={selectedDayFraction.value}
                  onChange={(e) => {
                    const fraction = Object.values(DAY_FRACTIONS).find(
                      (f) => f.value === e.target.value
                    );
                    setSelectedDayFraction(fraction);
                  }}
                >
                  {Object.values(DAY_FRACTIONS).map((fraction) => (
                    <MenuItem key={fraction.value} value={fraction.value}>
                      {fraction.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedDayFraction.value === 0 && (
                <TextField
                  fullWidth
                  label="Custom Hours per Day"
                  type="number"
                  value={customHours}
                  onChange={(e) =>
                    setCustomHours(parseFloat(e.target.value) || 8)
                  }
                  inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                label="Setup Time (hours)"
                type="number"
                value={setupTime}
                onChange={(e) => setSetupTime(parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.1 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Efficiency (%)"
                type="number"
                value={efficiency}
                onChange={(e) => setEfficiency(parseInt(e.target.value) || 100)}
                inputProps={{ min: 1, max: 200 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Live Preview */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Live Preview
              </Typography>
              {previewCalculation && (
                <Card sx={{ bgcolor: "#e3f2fd" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Zeitverteilung
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Days Required:</strong>{" "}
                      {previewCalculation.daysRequired}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Hours per Day:</strong>{" "}
                      {previewCalculation.hoursPerDay.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Hours:</strong>{" "}
                      {previewCalculation.totalHoursRequired.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Day Efficiency:</strong>{" "}
                      {previewCalculation.efficiency.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2">
                      <strong>Optimal Fraction:</strong>{" "}
                      {previewCalculation.optimalFraction.label}
                    </Typography>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Day-by-Day Breakdown:
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      {previewCalculation.dayBreakdown.map((day) => (
                        <Chip
                          key={day.day}
                          label={`Day ${day.day}: ${
                            day.menge
                          } units (${day.hours.toFixed(1)}h)`}
                          size="small"
                          sx={{ m: 0.5 }}
                          color={
                            day.fraction <= 0.5
                              ? "success"
                              : day.fraction <= 1
                              ? "warning"
                              : "error"
                          }
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button variant="outlined" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleSaveConfiguration}>
            Konfiguration Speichern
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApplyToCalendar}
            startIcon={<CalendarViewMonthIcon />}
          >
            Auf Kalender Anwenden
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// ProcessLegend Component
const ProcessLegend = ({ processes }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <StyledPaper>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6">Prozesslegende</Typography>
        <IconButton onClick={() => setExpanded(!expanded)} size="small">
          {expanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {expanded && (
        <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
          {processes.map((process) => (
            <Box
              key={process._id || process.id}
              display="flex"
              alignItems="center"
              mb={1}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: 1,
                  bgcolor: processColors[process.id] || "#1976d2",
                  mr: 1,
                }}
              />
              <Typography variant="body2" noWrap>
                {process.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </StyledPaper>
  );
};

// Define processData with processes and availableProcesses
const processData = {
  processes: [
    {
      id: 1,
      label:
        "Materialeingang / Materialausgang mit Kontrolle und Weitergabe an die Produktion incl. Auftragsnummer",
      names: [],
      color: "#1976d2",
    },
    {
      id: 2,
      label: "Baugruppe montieren",
      names: [],
      color: "#ff9800",
    },
    {
      id: 3,
      label: "Maschine einrichten",
      names: [],
      color: "#2e7d32",
    },
  ],
  availableProcesses: [
    {
      id: 4,
      label: "Materialzuschnitt",
      names: [],
      color: "#d32f2f",
    },
    {
      id: 5,
      label: "CNC-Bearbeitung",
      names: [],
      color: "#7b1fa2",
    },
    {
      id: 6,
      label: "Schweißarbeiten",
      names: [],
      color: "#0288d1",
    },
    {
      id: 7,
      label: "Qualitätskontrolle",
      names: [],
      color: "#ef6c00",
    },
    {
      id: 8,
      label: "Lackierung",
      names: [],
      color: "#00796b",
    },
    {
      id: 9,
      label: "Endmontage",
      names: [],
      color: "#c2185b",
    },
  ],
};

// Main CombinedCalendarMain Component
const CombinedCalendarMain = () => {
  const [templates, setTemplates] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [selectedANR, setSelectedANR] = useState(null);
  const [anrModalOpen, setAnrModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [attendanceData, setAttendanceData] = useState([]);
  const [machines, setMachines] = useState([]);
  const [combinedChains, setCombinedChains] = useState({});
  const [brokenChains, setBrokenChains] = useState({});
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [anrOptions, setAnrOptions] = useState([]);
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);
  const [remainingLohnaufwand, setRemainingLohnaufwand] = useState(0);

  const [projectMengeData, setProjectMengeData] = useState(null);
  const [processMengeConfig, setProcessMengeConfig] = useState({});
  const [mengeModalOpen, setMengeModalOpen] = useState(false);
  const [mengeConfigModalOpen, setMengeConfigModalOpen] = useState(false);
  const [selectedProcessForMenge, setSelectedProcessForMenge] = useState(null);
  const [mengeCalculations, setMengeCalculations] = useState({});
  const [workDayHours, setWorkDayHours] = useState(8); // Standard 8-hour work day
  const [anrAssignTarget, setAnrAssignTarget] = useState(null);

  // ========== NEW STATE VARIABLES ==========
  // Chain Preview State
  const [chainPreview, setChainPreview] = useState(null);
  const [isDraggingChainProcess, setIsDraggingChainProcess] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);

  // Filter State
  const [activeFilters, setActiveFilters] = useState({
    machines: [],
    employees: [],
    processTypes: [],
    chainStatus: [],
  });

  // Batch Selection State
  const [batchMode, setBatchMode] = useState(false);
  const [selectedProcessIdsForBatch, setSelectedProcessIdsForBatch] = useState(
    new Set()
  );

  // Drawer State
  const [tasksDrawerOpen, setTasksDrawerOpen] = useState(false);

  // Template Selection Modal State
  const [templateSelectionModalOpen, setTemplateSelectionModalOpen] =
    useState(false);
  const [selectedDateForTemplate, setSelectedDateForTemplate] = useState(null);
  const [selectedTemplateForApply, setSelectedTemplateForApply] =
    useState(null);
  const [selectedAnrForTemplate, setSelectedAnrForTemplate] = useState("");
  const [selectedAnrData, setSelectedAnrData] = useState(null);
  // ========== END NEW STATE ==========
  const [anrAssignData, setAnrAssignData] = useState(null);
  const [assignedTaskAnrs, setAssignedTaskAnrs] = useState({});
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [templateToCopy, setTemplateToCopy] = useState(null);
  const [pendingSplitProcesses, setPendingSplitProcesses] = useState(null);

  const DAY_FRACTIONS = {
    FULL: { value: 1, label: "Full Day (8h)", hours: 8 },
    HALF: { value: 0.5, label: "Half Day (4h)", hours: 4 },
    THIRD: { value: 1 / 3, label: "1/3 Day (2.67h)", hours: 8 / 3 },
    QUARTER: { value: 0.25, label: "Quarter Day (2h)", hours: 2 },
    CUSTOM: { value: 0, label: "Custom Hours", hours: 0 },
  };

  const [selectedDayFraction, setSelectedDayFraction] = useState(
    DAY_FRACTIONS.FULL
  );

  // Animated remaining Lohnaufwand
  const animatedProps = useSpring({
    number: remainingLohnaufwand,
    from: { number: 0 },
    config: { tension: 170, friction: 26 },
  });

  const calculateMengeTimeDistribution = (
    totalMenge,
    hoursPerMenge,
    mengePerDay
  ) => {
    const allowedHoursPerDay = selectedDayFraction?.hours || 8;
    const totalHoursRequired = totalMenge * hoursPerMenge;
    const daysByTime = Math.ceil(totalHoursRequired / allowedHoursPerDay);
    const daysByUnits = Math.ceil(totalMenge / mengePerDay);
    const daysRequired = Math.max(daysByTime, daysByUnits);
    const hoursPerDay = totalHoursRequired / daysRequired;

    let optimalFraction = DAY_FRACTIONS.FULL;
    if (hoursPerDay <= 2) optimalFraction = DAY_FRACTIONS.QUARTER;
    else if (hoursPerDay <= 8 / 3) optimalFraction = DAY_FRACTIONS.THIRD;
    else if (hoursPerDay <= 4) optimalFraction = DAY_FRACTIONS.HALF;

    let remainingHours = totalHoursRequired;
    let remainingUnits = totalMenge;

    const breakdown = [];
    let day = 1;

    while (remainingHours > 0) {
      const maxHoursByUnits = mengePerDay * hoursPerMenge;
      const dayHours = Math.min(
        remainingHours,
        allowedHoursPerDay,
        maxHoursByUnits
      );
      const dayMenge = dayHours / hoursPerMenge;

      breakdown.push({
        day,
        menge: dayMenge,
        hours: dayHours,
        fraction: dayHours / allowedHoursPerDay,
        optimalFraction,
      });

      remainingHours -= dayHours;
      remainingUnits -= dayMenge;
      day++;
    }

    return {
      totalMenge,
      hoursPerMenge,
      mengePerDay,
      totalHoursRequired,
      daysRequired,
      hoursPerDay,
      optimalFraction,
      efficiency: Math.min(100, (hoursPerDay / 8) * 100),
      dayBreakdown: breakdown,
    };
  };

  const onApplyProcessTime = (process, processTotalTime) => {
    setSelectedProcessForMenge(process);

    const calculation = calculateDayBreakdown(
      processTotalTime.startupTime / 60,
      processTotalTime.processingTime / 60,
      workDayHours
    );

    const processKey = `${process.id}-${process.label || ""}`;

    console.log("Applying process time for:", processKey, calculation);

    setMengeCalculations((prev) => ({
      ...prev,
      [processKey]: {
        ...calculation,
        processKey,
        lastCalculated: new Date(),
      },
    }));
  };

  useEffect(() => {
    const scheduledDate = Object.entries(calendarData).find(
      ([dateKey, dayData]) =>
        dayData.processes?.some(
          (process) =>
            process._id && process._id === selectedProcessForMenge._id
        )
    )?.[0];

    if (scheduledDate) {
      handleDropProcess(
        selectedProcessForMenge,
        new Date(scheduledDate),
        selectedProcessForMenge.chainId
      );
    }
  }, [mengeCalculations]);

  // Handle pending split processes after templates are updated
  useEffect(() => {
    if (
      pendingSplitProcesses &&
      pendingSplitProcesses.splitProcesses.length > 0
    ) {
      const { splitProcesses, scheduledDate } = pendingSplitProcesses;

      // Now that templates are updated, call handleDropProcess for each split process
      splitProcesses.forEach((splitProcess) => {
        handleDropProcess(splitProcess, scheduledDate, splitProcess.chainId);
      });

      // Clear pending split processes
      setPendingSplitProcesses(null);
    }
  }, [templates, pendingSplitProcesses]);

  function calculateDayBreakdown(processTime, startupTime, workDayHours = 8) {
    const totalHours = (processTime || 0) + (startupTime || 0);
    const daysRequired = Math.ceil(totalHours / workDayHours);

    const breakdown = [];
    let remaining = totalHours;

    const optimalFraction = DAY_FRACTIONS.FULL;
    for (let day = 1; day <= daysRequired; day++) {
      const hours = Math.min(remaining, workDayHours);
      const fraction = hours / workDayHours;

      breakdown.push({
        day,
        hours,
        daysRequired,
        optimalFraction,
      });

      remaining -= hours;
    }

    const returnObj = {
      totalHours,
      daysRequired,
      workDayHours,
      optimalFraction,
      efficiency: Math.min(100, (workDayHours / 8) * 100),
      dayBreakdown: breakdown,
    };

    return returnObj;
  }

  /**
   * Calculate cost implications of Menge-based planning
   */
  const calculateMengeCostAnalysis = (config) => {
    const {
      totalMenge,
      hoursPerMenge,
      hourlyRate = 50,
      setupCost = 0,
      materialCostPerMenge = 0,
    } = config;

    const laborCost = totalMenge * hoursPerMenge * hourlyRate;
    const materialCost = totalMenge * materialCostPerMenge;
    const totalCost = laborCost + materialCost + setupCost;

    return {
      laborCost,
      materialCost,
      setupCost,
      totalCost,
      costPerMenge: totalCost / totalMenge,
    };
  };

  // Function to handle copying a template
  const handleCopyTemplate = (template) => {
    setTemplateToCopy(template);
    setCopyModalOpen(true);
  };

  // Function to perform the copy after new name is provided
  const performCopyTemplate = async (newName) => {
    if (!templateToCopy) return;

    // Extract existing numeric IDs from the template's processes
    const existingIds = templateToCopy.processes.map((process) => process.id);

    // Determine the next available ID
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    let nextId = maxId + 1;

    // Assign new numeric IDs to duplicated processes
    const duplicatedProcesses = templateToCopy.processes.map(
      (process, index) => ({
        ...process,
        id: nextId++,
        chainId: process.chainId,
        chainOrder: index,
        scheduledDate: null,
      })
    );

    // Assign nextProcessId accordingly
    duplicatedProcesses.forEach((process, index) => {
      if (index < duplicatedProcesses.length - 1) {
        process.nextProcessId = duplicatedProcesses[index + 1].id;
      } else {
        process.nextProcessId = null;
      }
    });

    // Prepare the payload
    const payload = {
      name: newName,
      processes: duplicatedProcesses,
    };

    try {
      const response = await axios.post(
        `${base_url}/v1/CreateCaledarTemplate`,
        payload
      );
      if (response.status === 201) {
        const newTemplate = response.data.template;
        setTemplates((prevTemplates) => [...prevTemplates, newTemplate]);
        initializeCalendarData([...templates, newTemplate]);
        toast.success("Template erfolgreich kopiert und umbenannt!");
      } else {
        toast.error(
          "Fehler beim Kopieren des Templates. Bitte versuchen Sie es erneut."
        );
      }
    } catch (error) {
      console.error("Error copying template:", error);
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setCopyModalOpen(false);
      setTemplateToCopy(null);
    }
  };

  // Delete a template
  const deleteTemplate = async (templateId) => {
    try {
      const response = await axios.delete(
        `${base_url}/v1/DeleteTemplate/${templateId}`
      );
      if (response.status === 200) {
        setTemplates((prevTemplates) =>
          prevTemplates.filter((template) => template._id !== templateId)
        );
        toast.success("Template deleted successfully!");
      } else {
        toast.error("Failed to delete template. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("An error occurred while deleting the template.");
    }
  };

  // Fetch Machines
  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllMachines`);
        setMachines(response.data);
      } catch (error) {
        console.error("Error fetching machines:", error);
        toast.error("Failed to fetch machines.");
      }
    };
    fetchMachines();
  }, []);

  // Fetch Attendance Data
  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getDailyAttendance`);
        setAttendanceData(response.data.data);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
        setAttendanceData([]);
        toast.error("Failed to fetch attendance data.");
      }
    };
    fetchAttendanceData();
  }, []);

  // Fetch Templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/GetCaledarTemplate`);

        const updatedTemplates = response.data.map(
          (template, templateIndex) => {
            const chainId = `chain-${templateIndex}-${uuidv4()}`;

            const updatedProcesses = template.processes.map(
              (process, index) => {
                // 1) Extract employees -> "names" array
                const namesFromAssigned =
                  process.assignedEmployees?.map((ae) => ae.employee?.name) ||
                  [];

                // 2) Extract first machine -> "machinename" string
                const machineName =
                  process.assignedEmployees &&
                  process.assignedEmployees.length > 0
                    ? process.assignedEmployees[0].machine?.prozess || ""
                    : "Keine Maschine zugewiesen.";

                return {
                  ...process,

                  // maintain chain data
                  chainId: chainId,
                  scheduledDate: null,
                  chainOrder: index,
                  type: ItemTypes.PROCESS,
                  color: processColors[process.id] || "#1976d2",

                  // new simpler fields for your UI
                  names: namesFromAssigned,
                  machinename: machineName,
                };
              }
            );

            // link processes in the chain
            for (let i = 0; i < updatedProcesses.length - 1; i++) {
              updatedProcesses[i].nextProcessId = updatedProcesses[i + 1].id;
            }

            return { ...template, processes: updatedProcesses };
          }
        );

        setTemplates(updatedTemplates);

        initializeCalendarData(updatedTemplates);
      } catch (error) {
        console.error("Error fetching templates:", error);
        toast.error("Failed to fetch templates.");
      }
    };

    fetchTemplates();
  }, []);

  // Fetch Employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllEmployees`);
        setEmployees(response.data);
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Failed to fetch employees.");
      }
    };
    fetchEmployees();
  }, []);

  // Fetch Groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllGroups`);
        setGroups(response.data);
      } catch (error) {
        console.error("Error fetching groups:", error);
        toast.error("Failed to fetch groups.");
      }
    };
    fetchGroups();
  }, []);

  // Fetch All ANRs for template selection
  useEffect(() => {
    const fetchAllAnrs = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllProjects`);
        if (response.data.status === 1) {
          const allProjects = response.data.data;
          const anrs = allProjects.map((project) => ({
            anr: project.ANR,
            auftragsbezeichnung:
              project.Auftragsbezeichnung || "No description",
            projectId: project._id,
          }));
          setAnrOptions(anrs);
        }
      } catch (error) {
        console.error("Error fetching ANRs:", error);
        toast.error("Failed to fetch ANRs.");
      }
    };
    // fetchAllAnrs();
  }, []);

  // Initialize Calendar Data with improved error handling
  const initializeCalendarData = (templates) => {
    const newCalendarData = {};

    templates.forEach((template) => {
      if (!template.processes) {
        console.warn("Template has no processes:", template);
        return;
      }

      template.processes.forEach((process) => {
        if (process.scheduledDate) {
          const date = new Date(process.scheduledDate);
          if (isNaN(date)) {
            console.warn(
              `Invalid date format for process ID ${process.id}: ${process.scheduledDate}`
            );
            return;
          }
          const dateKey = getISODateString(date);
          if (!newCalendarData[dateKey]) {
            newCalendarData[dateKey] = { processes: [], tasks: [], events: [] };
          }

          // Ensure all necessary properties are included
          newCalendarData[dateKey].processes.push({
            ...process,
            scheduledDate: date,
            type: ItemTypes.PROCESS, // Ensure type is set
            chainId: process.chainId || `chain-${uuidv4()}`, // Ensure chainId exists
          });
        }
      });
    });

    console.log("Initialized calendar data:", newCalendarData);
    setCalendarData(newCalendarData);
  };

  // Update Process Employee
  const updateProcessEmployee = (templateId, processId, newEmployee) => {
    setTemplates((prevTemplates) =>
      prevTemplates.map((template) => {
        if (template._id !== templateId) return template;

        const updatedProcesses = template.processes.map((process) => {
          if (process.id !== processId) return process;
          return {
            ...process,
            names: [newEmployee] || [],
          };
        });

        return {
          ...template,
          processes: updatedProcesses,
        };
      })
    );
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      Object.keys(newCalendarData).forEach((dateKey) => {
        newCalendarData[dateKey].processes = newCalendarData[
          dateKey
        ].processes.map((process) =>
          (process._id && process._id === processId) || process.id === processId
            ? {
                ...process,
                names: [newEmployee],
                assignedEmployees: process.assignedEmployees
                  ? [
                      {
                        ...process.assignedEmployees[0],
                        employee: {
                          ...process.assignedEmployees[0]?.employee,
                          name: newEmployee,
                        },
                      },
                    ]
                  : [],
              }
            : process
        );
      });
      return newCalendarData;
    });
  };

  const updateProcessMachine = (templateId, processId, newMachine) => {
    setTemplates((prevTemplates) =>
      prevTemplates.map((template) => {
        if (template._id !== templateId) return template;

        const updatedProcesses = template.processes.map((process) => {
          if (process.id !== processId) return process;
          return {
            ...process,
            machinename: newMachine || "",
          };
        });

        return {
          ...template,
          processes: updatedProcesses,
        };
      })
    );

    // Find the selected machine by multiple criteria to ensure proper matching
    const selectedMachine = machines.find(
      (m) =>
        m.prozess === newMachine ||
        m.name === newMachine ||
        m._id === newMachine
    );

    if (!selectedMachine && newMachine) {
      console.warn("Machine not found in machines list:", newMachine);
      toast.error("Maschine nicht gefunden. Bitte versuchen Sie es erneut.");
      return;
    }

    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      let updated = false;

      Object.keys(newCalendarData).forEach((dateKey) => {
        newCalendarData[dateKey].processes = newCalendarData[
          dateKey
        ].processes.map((process) => {
          const matches =
            (process._id && process._id === processId) ||
            (process.id && process.id === processId) ||
            (process.instanceId && process.instanceId === processId);

          if (matches) {
            updated = true;

            return {
              ...process,
              // Update multiple machine reference fields for compatibility
              machinename:
                selectedMachine?.prozess || selectedMachine?.name || "",
              machine: selectedMachine,
              machineId: selectedMachine?._id,
              // Update assignedEmployees structure
              assignedEmployees:
                process.assignedEmployees &&
                process.assignedEmployees.length > 0
                  ? process.assignedEmployees.map((emp) => ({
                      ...emp,
                      machine: selectedMachine,
                      machineData: {
                        id: selectedMachine?._id,
                        name: selectedMachine?.name,
                        prozess: selectedMachine?.prozess,
                      },
                    }))
                  : selectedMachine
                  ? [
                      {
                        name: "Nicht zugewiesen",
                        machine: selectedMachine,
                        machineData: {
                          id: selectedMachine._id,
                          name: selectedMachine.name,
                          prozess: selectedMachine.prozess,
                        },
                      },
                    ]
                  : [],
            };
          }
          return process;
        });
      });

      if (updated) {
        toast.success(
          `Maschine erfolgreich zu ${
            selectedMachine?.prozess || "Prozess"
          } zugewiesen`
        );
      } else {
        console.warn("No process found with ID:", processId);
      }

      return newCalendarData;
    });
  };

  const fetchProjectMengeData = async (anrData) => {
    if (!anrData?.projectId) {
      console.warn("No project ID available for Menge data fetching");
      return;
    }

    try {
      // Fetch detailed project information including Menge data

      const projectResponse = await fetch(`${base_url}/v1/getProjectbyANR`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ ANR: anrData.anr }),
      });

      const result = await projectResponse.json();
      if (projectResponse.ok) {
        const projectData = result.data[0]; // Get first matching project

        // Extract Menge-related information
        const mengeData = {
          projectId: projectData._id,
          anr: anrData.anr,
          auftragsbezeichnung: projectData.Auftragsbezeichnung,
          totalMenge: parseFloat(projectData.Menge) || 0,
          mengeCost: parseFloat(projectData.MengeCost) || 0,
          singleMengeCost: parseFloat(projectData.singlemengecost) || 0,
          estimatedHours: parseFloat(projectData.EstimatedHours) || 0,
          estimatedCost: parseFloat(projectData.EstimatedCost) || 0,
          percentDone: parseFloat(projectData.percentDone) || 0,
          status: projectData.status || "ausstehend",
          deadline: projectData.deadline,

          // Calculate derived values
          remainingMenge: 0, // Will be calculated based on completion
          hoursPerMenge: 0, // Will be calculated or configured per process

          // Additional project metadata
          customer: projectData.kundenID,
          materialCosts: projectData.materialCosts || [],
          subcontractors: projectData.subcontractors || [],
        };

        // Calculate derived values
        if (mengeData.totalMenge > 0) {
          mengeData.remainingMenge =
            mengeData.totalMenge * (1 - mengeData.percentDone / 100);
          if (mengeData.estimatedHours > 0) {
            mengeData.hoursPerMenge =
              mengeData.estimatedHours / mengeData.totalMenge;
          }
        }

        setProjectMengeData(mengeData);

        // Initialize process-specific Menge-Konfiguration
        initializeProcessMengeConfig(mengeData);

        console.log("✅ Menge data loaded successfully:", mengeData);
        toast.success(
          `Menge data loaded: ${
            mengeData.totalMenge
          } units (${mengeData.remainingMenge.toFixed(1)} remaining)`
        );
      } else {
        console.warn("No project data found for ANR:", anrData.anr);
        toast.warning("No Menge data found for selected ANR");
        setProjectMengeData(null);
      }
    } catch (error) {
      console.error("❌ Error fetching Menge data:", error);
      toast.error("Failed to load Menge data for selected ANR");
      setProjectMengeData(null);
    }
  };

  /**
   * Initialize process-specific Menge-Konfiguration
   * Sets up default time per Menge for each process type
   */
  const initializeProcessMengeConfig = (mengeData) => {
    if (!templates || !mengeData) return;

    const config = {};

    templates.forEach((template) => {
      if (template.processes) {
        template.processes.forEach((process) => {
          const processKey = `${process.id}-${process.label}`;
          config[processKey] = {
            processId: process._id,
            processLabel: process.label,
            hoursPerMenge: mengeData.hoursPerMenge || 1, // Default 1 hour per Menge
            mengePerDay: 1, // Default 1 Menge per day
            dayFraction: DAY_FRACTIONS.FULL, // Default full day
            customHours: null,
            isConfigured: false,

            // Process-specific overrides
            setupTime: 0, // Setup time per batch
            cleanupTime: 0, // Cleanup time per batch
            efficiency: 100, // Process efficiency percentage

            // Calculated values (will be computed when needed)
            totalHoursForProcess: 0,
            daysRequired: 0,
            costPerMenge: 0,
          };
        });
      }
    });

    setProcessMengeConfig(config);
    console.log("📋 Process Menge-Konfiguration initialized:", config);
  };

  /**
   * Open Menge-Konfiguration modal for specific process
   */
  const openMengeConfigForProcess = (process) => {
    if (!projectMengeData) {
      toast.warning("Please select an ANR first to load Menge data");
      return;
    }

    setSelectedProcessForMenge(process);
    setMengeConfigModalOpen(true);
  };

  /**
   * Update Menge-Konfiguration for a specific process
   */
  const updateProcessMengeConfig = (processId, configUpdate) => {
    const processKey = `${processId}-${configUpdate.processLabel || ""}`;

    setProcessMengeConfig((prev) => ({
      ...prev,
      [processKey]: {
        ...prev[processKey],
        ...configUpdate,
        isConfigured: true,
        lastUpdated: new Date(),
      },
    }));

    // Recalculate Zeitverteilung for this process
    recalculateProcessTimeDistribution(processKey, configUpdate);
  };

  /**
   * Recalculate Zeitverteilung when Menge-Konfiguration changes
   */
  const recalculateProcessTimeDistribution = (processKey, config) => {
    if (!projectMengeData || !config) return;

    const calculation = calculateMengeTimeDistribution(
      projectMengeData.remainingMenge,
      config.hoursPerMenge,
      config.mengePerDay
    );

    setMengeCalculations((prev) => ({
      ...prev,
      [processKey]: {
        ...calculation,
        processKey,
        costAnalysis: calculateMengeCostAnalysis({
          totalMenge: projectMengeData.remainingMenge,
          hoursPerMenge: config.hoursPerMenge,
          hourlyRate: 50, // This could be made configurable
          setupCost: config.setupTime * 50 || 0,
          materialCostPerMenge: projectMengeData.singleMengeCost || 0,
        }),
        lastCalculated: new Date(),
      },
    }));
  };

  // Assign ANR to Template
  const assignAnrToTemplate = (anrData) => {
    if (!anrData || !anrAssignData) return;

    const templateId = anrAssignData;

    const updatedTemplates = templates.map((template) => {
      if (template._id === templateId) {
        return {
          ...template,
          assignedANR: anrData,
          processes: template.processes.map((process) => ({
            ...process,
            anr: anrData.anr,
            auftragsbezeichnung: anrData.auftragsbezeichnung,
          })),
        };
      }
      return template;
    });

    setTemplates(updatedTemplates);
    initializeCalendarData(updatedTemplates);

    // Update remaining Lohnaufwand
    setRemainingLohnaufwand(anrData.maxLohnaufwand * 60 || 0);

    // Also, set selectedANR with projectId
    setSelectedANR(anrData);

    // === FETCH MENGE DATA AFTER ANR SELECTION ===
    // Professional Menge data loading with comprehensive project information
    fetchProjectMengeData(anrData);

    // Update ANR options (avoid duplicates)
    setAnrOptions((prev) => {
      const exists = prev.some((option) => option.anr === anrData.anr);
      if (exists) {
        return prev;
      }
      return [
        ...prev,
        {
          anr: anrData.anr,
          auftragsbezeichnung: anrData.auftragsbezeichnung,
          projectId: anrData.projectId,
        },
      ];
    });

    toast.success("ANR assigned to template successfully!");
  };

  // Implement handleDeleteProcess function
  const handleDeleteProcess = (process) => {
    // Remove from calendarData
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      Object.keys(newCalendarData).forEach((dateKey) => {
        if (newCalendarData[dateKey].processes) {
          newCalendarData[dateKey].processes = newCalendarData[
            dateKey
          ].processes.filter(
            (p) => p._id !== process._id && p.id !== process.id
          );
        }
      });
      return newCalendarData;
    });

    // Remove from templates
    setTemplates((prevTemplates) => {
      return prevTemplates.map((template) => {
        return {
          ...template,
          processes: template.processes.filter((p) => p.id !== process.id),
        };
      });
    });

    toast.success("Process deleted successfully!");
  };

  // Split a process into half-day segments
  const handleSplitProcess = (process) => {
    if (!process) {
      toast.error("Fehler: Prozess nicht gefunden");
      return;
    }

    const totalHours = process.hours || 8;
    const halfDayHours = 4; // Half day = 4 hours
    const numberOfHalfDays = Math.ceil(totalHours / halfDayHours);

    if (numberOfHalfDays <= 1) {
      toast.warning("Prozess hat bereits die minimale Dauer (≤4h)");
      return;
    }

    // Get the scheduled date of the process
    const scheduledDate = process.scheduledDate
      ? typeof process.scheduledDate === "string"
        ? new Date(process.scheduledDate)
        : process.scheduledDate
      : new Date();

    // Create half-day process instances
    const splitProcesses = [];
    let remainingHours = totalHours;
    let currentDate = new Date(scheduledDate);
    let dayHoursUsed = 0;

    for (let i = 0; i < numberOfHalfDays; i++) {
      const hoursForThisSegment = Math.min(halfDayHours, remainingHours);

      // Check if we need to move to next day (max 8 hours per day)
      if (dayHoursUsed + hoursForThisSegment > 4) {
        currentDate = addWorkingDays(currentDate, 1);
        dayHoursUsed = 0;
      }

      const splitProcess = {
        ...process,
        _id: process._id,
        id: process.id,
        instanceId: `${process.id || process._id}-half-${i}-${Date.now()}`,
        calendarId: `${process.id || process._id}-half-${i}-${Date.now()}`,
        chainId: process.chainId,
        chainOrder: process.chainOrder,
        hours: hoursForThisSegment,
        isHalfDay: true,
        halfDayIndex: i,
        totalHalfDays: numberOfHalfDays,
        originalHours: totalHours,
        scheduledDate: new Date(currentDate),
        label: process.label || process.anr,
        anr: process.anr,
        auftragsbezeichnung: process.auftragsbezeichnung,
        assignedEmployees: process.assignedEmployees || [],
      };

      splitProcesses.push(splitProcess);
      remainingHours -= hoursForThisSegment;
      dayHoursUsed += hoursForThisSegment;
    }

    console.log(
      `📅 Split process into ${splitProcesses.length} half-day segments:`,
      splitProcesses
    );

    // Helper function to update templates with split processes
    const getUpdatedTemplates = (prevTemplates) => {
      return prevTemplates.map((template) => {
        const hasProcess = template.processes.some(
          (p) => p._id === process._id || p.id === process.id
        );
        if (hasProcess) {
          // Replace the original process with split processes
          const updatedProcesses = template.processes.filter(
            (p) => p._id !== process._id && p.id !== process.id
          );
          return {
            ...template,
            processes: [...updatedProcesses, ...splitProcesses],
          };
        }
        return template;
      });
    };

    setTemplates((prevTemplates) => getUpdatedTemplates(prevTemplates));

    setPendingSplitProcesses({
      splitProcesses,
      scheduledDate,
      originalProcess: process,
    });
  };

  // Assign ANR to Task
  const assignAnrToTask = (taskId, anrData) => {
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      Object.keys(newCalendarData).forEach((dateKey) => {
        newCalendarData[dateKey].tasks = newCalendarData[dateKey].tasks.map(
          (task) =>
            task.id === taskId
              ? {
                  ...task,
                  anr: anrData.anr,
                  auftragsbezeichnung: anrData.auftragsbezeichnung,
                }
              : task
        );
      });
      return newCalendarData;
    });

    // Map the assigned ANR to the task ID
    setAssignedTaskAnrs((prev) => ({
      ...prev,
      [taskId]: anrData.anr,
    }));

    toast.success("ANR assigned to task successfully!");
  };

  // Handle ANR Selection Based on Target
  const handleAnrSelect = (anrData) => {
    if (anrAssignTarget === "template") {
      assignAnrToTemplate(anrData);
    } else if (anrAssignTarget === "task") {
      if (anrAssignData && anrAssignData.taskId) {
        assignAnrToTask(anrAssignData.taskId, anrData);
      }
    }
    // Reset assignment target and data
    setAnrAssignTarget(null);
    setAnrAssignData(null);
  };

  // Function to Open ANR Modal for Template
  const openAnrModalForTemplate = (templateId) => {
    setAnrAssignTarget("template");
    setAnrAssignData(templateId);
    setAnrModalOpen(true);
  };

  // Function to Open ANR Modal for Task
  const openAnrModalForTask = (taskId) => {
    setAnrAssignTarget("task");
    setAnrAssignData({ taskId });
    setAnrModalOpen(true);
  };

  // Update Remaining Lohnaufwand when scheduled processes change
  useEffect(() => {
    if (!selectedANR) return;

    // Filter processes associated with the current ANR
    const scheduledProcessesForANR = Object.values(calendarData)
      .flatMap((day) => day.processes)
      .filter(
        (process) => process.scheduledDate && process.anr === selectedANR.anr
      );

    const totalScheduledProcessHours = scheduledProcessesForANR.reduce(
      (sum, process) => sum + process.hours,
      0
    );

    // Include 'paid' tasks
    const scheduledTasksForANR = Object.values(calendarData)
      .flatMap((day) => day.tasks)
      .filter(
        (task) =>
          task.scheduledDate && task.anr === selectedANR.anr && task.paid
      );

    const totalScheduledTaskHours = scheduledTasksForANR.reduce(
      (sum, task) => sum + task.hours,
      0
    );

    const totalScheduledHours =
      (totalScheduledProcessHours + totalScheduledTaskHours) * 60;

    const initialLohnaufwand = selectedANR.maxLohnaufwand * 60 || 0;

    // Allow Lohnaufwand to go negative
    setRemainingLohnaufwand(initialLohnaufwand - totalScheduledHours);
  }, [calendarData, selectedANR]);

  // Handle Save
  // ============================================================================
  // BULLETPROOF MONGODB SAVE - Handles All Process Types (Recurring, Multi-day, Chain)
  // ============================================================================
  const handleSave = async () => {
    try {
      console.log(
        "🚀 BULLETPROOF MONGODB SAVE: Starting comprehensive save operation"
      );

      // Critical validation: Check if project is selected
      if (!selectedANR || !selectedANR.projectId) {
        toast.error(
          "Bitte wählen Sie ein Projekt (ANR) vor dem Speichern aus."
        );
        console.error("❌ Kein Projekt zum Speichern ausgewählt");
        return;
      }

      console.log("✅ Project validation passed:", {
        projectId: selectedANR.projectId,
        anr: selectedANR.anr,
        auftragsbezeichnung: selectedANR.auftragsbezeichnung,
      });

      // Comprehensive data collection from calendar
      const allProcesses = Object.values(calendarData).flatMap(
        (day) => day.processes || []
      );
      const allTasks = Object.values(calendarData).flatMap(
        (day) => day.tasks || []
      );
      const allEvents = Object.values(calendarData).flatMap(
        (day) => day.events || []
      );

      console.log(`📊 Data Collection Summary:`, {
        totalProcesses: allProcesses.length,
        totalTasks: allTasks.length,
        totalEvents: allEvents.length,
        calendarDates: Object.keys(calendarData).length,
      });

      // Enhanced data validation
      if (
        allProcesses.length === 0 &&
        allTasks.length === 0 &&
        allEvents.length === 0
      ) {
        toast.error(
          "Keine Prozesse, Aufgaben oder Events zum Speichern vorhanden."
        );
        console.warn("⚠️ No data to save");
        return;
      }

      // Analyze process types for comprehensive handling
      const processAnalysis = analyzeProcessTypes(allProcesses);
      console.log("🔍 Process Type Analysis:", processAnalysis);

      // Enhanced validation for ANR assignments with detailed reporting
      const validationResults = validateProcessDataIntegrity(
        allProcesses,
        allTasks,
        selectedANR
      );
      if (validationResults.warnings.length > 0) {
        console.warn(
          "⚠️ Data validation warnings:",
          validationResults.warnings
        );
        validationResults.warnings.forEach((warning) => {
          toast.warning(warning, { autoClose: 3000 });
        });
      }

      // COMPREHENSIVE PROCESS TRANSFORMATION FOR MONGODB
      const transformedProcesses = transformProcessesForMongoDB(
        allProcesses,
        selectedANR,
        processAnalysis,
        processMengeConfig,
        mengeCalculations
      );

      const transformedTasks = transformTasksForMongoDB(allTasks, selectedANR);
      const transformedEvents = transformEventsForMongoDB(
        allEvents,
        selectedANR
      );

      // Final validation before API call
      const finalValidation = validateTransformedData(
        transformedProcesses,
        transformedTasks,
        transformedEvents
      );
      if (!finalValidation.isValid) {
        console.error("❌ Final validation failed:", finalValidation.errors);
        toast.error(`Validierungsfehler: ${finalValidation.errors.join(", ")}`);
        return;
      }

      console.log("📋 Final MongoDB Payload:", {
        projectId: selectedANR.projectId,
        processesCount: transformedProcesses.length,
        tasksCount: transformedTasks.length,
        eventsCount: transformedEvents.length,
        sampleProcess: transformedProcesses[0],
        processTypes: finalValidation.processTypes,
      });

      // Make API call to save planning with enhanced error handling
      const response = await savePlanningToMongoDB({
        projectId: selectedANR.projectId,
        processes: transformedProcesses,
        tasks: transformedTasks,
        events: transformedEvents,
        metadata: {
          saveTimestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          calendarDatesCount: Object.keys(calendarData).length,
          processAnalysis: processAnalysis,
          validationResults: validationResults,
        },
      });

      if (response.success) {
        toast.success(
          `✅ Kalender erfolgreich gespeichert! ${transformedProcesses.length} Prozesse, ${transformedTasks.length} Aufgaben, ${transformedEvents.length} Events in MongoDB gespeichert.`
        );
        console.log("🎉 MongoDB Save Successful:", response.data);
      } else {
        throw new Error(response.error || "Unbekannter Server-Fehler");
      }
    } catch (error) {
      console.error("❌ MONGODB SAVE ERROR:", {
        error: error,
        message: error.message,
        stack: error.stack,
        selectedANR: selectedANR,
        processCount: Object.values(calendarData).flatMap(
          (day) => day.processes || []
        ).length,
      });

      const errorMessage = getFormattedErrorMessage(error);
      toast.error(`Speichern fehlgeschlagen: ${errorMessage}`);
    }
  };

  const analyzeProcessTypes = (processes) => {
    const analysis = {
      total: processes.length,
      singleDay: 0,
      multiDay: 0,
      chainProcesses: 0,
      recurringProcesses: 0,
      processesWithMenge: 0,
      processesWithANR: 0,
      processesWithEmployees: 0,
      processesWithMachines: 0,
      uniqueChains: new Set(),
      uniqueLabels: new Set(),
      dateRange: {
        earliest: null,
        latest: null,
      },
    };

    processes.forEach((process) => {
      // Count process types
      if (process.isMultiDay) {
        analysis.multiDay++;
      } else {
        analysis.singleDay++;
      }

      if (process.chainId) {
        analysis.chainProcesses++;
        analysis.uniqueChains.add(process.chainId);
      }

      if (process.instanceNumber && process.instanceNumber > 1) {
        analysis.recurringProcesses++;
      }

      if (process.mengeForDay || process.totalMengeForProcess) {
        analysis.processesWithMenge++;
      }

      if (process.anr && process.anr !== "Not Assigned") {
        analysis.processesWithANR++;
      }

      if (process.names && process.names.length > 0) {
        analysis.processesWithEmployees++;
      }

      if (process.machinename) {
        analysis.processesWithMachines++;
      }

      analysis.uniqueLabels.add(process.label || process.originalLabel);

      // Track date range
      if (process.scheduledDate) {
        const date = new Date(process.scheduledDate);
        if (
          !analysis.dateRange.earliest ||
          date < analysis.dateRange.earliest
        ) {
          analysis.dateRange.earliest = date;
        }
        if (!analysis.dateRange.latest || date > analysis.dateRange.latest) {
          analysis.dateRange.latest = date;
        }
      }
    });

    analysis.uniqueChains = analysis.uniqueChains.size;
    analysis.uniqueLabels = analysis.uniqueLabels.size;

    return analysis;
  };

  // Validate process data integrity
  const validateProcessDataIntegrity = (processes, tasks, selectedANR) => {
    const warnings = [];
    const errors = [];

    // Check ANR assignments
    const processesWithDates = processes.filter((p) => p.scheduledDate);
    const processesMissingANR = processesWithDates.filter(
      (p) => !p.anr || p.anr === "Not Assigned"
    );

    if (processesMissingANR.length > 0) {
      warnings.push(
        `${processesMissingANR.length} Prozesse haben keine ANR-Zuweisung`
      );
    }

    // Check for duplicate process IDs
    const processIds = processes
      .map((p) => p.instanceId || p._id || p.id)
      .filter(Boolean);
    const duplicateIds = processIds.filter(
      (id, index) => processIds.indexOf(id) !== index
    );
    if (duplicateIds.length > 0) {
      warnings.push(
        `Doppelte Prozess-IDs gefunden: ${duplicateIds.join(", ")}`
      );
    }

    // Check for missing required fields
    processes.forEach((process, index) => {
      if (!process.label && !process.originalLabel) {
        errors.push(`Prozess ${index + 1} hat kein Label`);
      }
      if (!process.id && !process._id) {
        errors.push(`Prozess ${index + 1} hat keine ID`);
      }
    });

    return { warnings, errors, isValid: errors.length === 0 };
  };

  // Transform processes for MongoDB with comprehensive data mapping
  const transformProcessesForMongoDB = (
    processes,
    selectedANR,
    analysis,
    mengeConfig = {},
    mengeCalculations = {}
  ) => {
    const processInstanceMap = new Map();

    return processes.map((process, globalIndex) => {
      // Create comprehensive unique identifier
      const baseId = process.id || process._id || `process-${globalIndex}`;
      const instanceId =
        process.instanceId ||
        process.calendarId ||
        `${baseId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Handle process grouping for recurring instances
      const processKey = `${
        process.label || process.originalLabel || "unnamed"
      }-${process.anr || "no-anr"}`;
      if (!processInstanceMap.has(processKey)) {
        processInstanceMap.set(processKey, 0);
      }
      const instanceNumber = processInstanceMap.get(processKey) + 1;
      processInstanceMap.set(processKey, instanceNumber);

      // Extract comprehensive employee information
      const extractedEmployees = extractEmployeeInformation(process);
      const extractedMachine = extractMachineInformation(process);

      // Get Menge-Konfiguration for this process
      const processConfigKey = `${process.id}-${process.label}`;
      const mengeConfigForProcess = mengeConfig[processConfigKey] || {};
      const mengeCalcForProcess = mengeCalculations[processConfigKey] || {};

      // Create comprehensive MongoDB document
      return {
        // === CORE IDENTIFICATION ===
        id: baseId,
        _id: process._id,
        instanceId: instanceId,

        // === PROCESS METADATA ===
        label: process.label || process.originalLabel || "Unnamed Process",
        originalLabel: process.originalLabel || process.label,
        description: process.description || "",

        // === TIMING & SCHEDULING ===
        hours: Math.max(0, process.hours || 0),
        scheduledDate: process.scheduledDate
          ? typeof process.scheduledDate === "string"
            ? process.scheduledDate
            : process.scheduledDate.toISOString()
          : null,

        // === PROJECT ASSIGNMENT ===
        anr: process.anr || selectedANR?.anr || "",
        auftragsbezeichnung:
          process.auftragsbezeichnung || selectedANR?.auftragsbezeichnung || "",
        projectId: selectedANR?.projectId || "",

        // === RESOURCE ASSIGNMENTS ===
        names: extractedEmployees.names,
        assignedEmployees: extractedEmployees.fullData,
        machinename: extractedMachine.name,
        machine: extractedMachine.fullData,
        machineId: extractedMachine.id,

        // === CHAIN & RELATIONSHIP DATA ===
        chainId: process.chainId || null,
        nextProcessId: process.nextProcessId || null,
        chainOrder: process.chainOrder || 0,
        chainOffset: process.chainOffset || 0,

        // === MULTI-DAY PROCESS SUPPORT ===
        isMultiDay: Boolean(process.isMultiDay),
        dayNumber: process.dayNumber || 1,
        totalDays: process.totalDays || 1,
        hoursForThisDay: process.hoursForThisDay || process.hours || 0,
        originalProcessId: process.originalProcessId || baseId,

        // === INSTANCE & RECURRING DATA ===
        isInstance: Boolean(process.isInstance || instanceNumber > 1),
        instanceNumber: instanceNumber,
        globalIndex: globalIndex,

        // === MENGE MANAGEMENT DATA ===
        mengeForDay:
          process.mengeForDay || mengeConfigForProcess.mengePerDay || 0,
        totalMengeForProcess:
          process.totalMengeForProcess || mengeCalcForProcess.totalMenge || 0,
        dayFraction:
          process.dayFraction || mengeConfigForProcess.dayFraction || 1,
        optimalFraction: mengeConfigForProcess.optimalFraction || null,
        costForDay: process.costForDay || mengeCalcForProcess.costPerDay || 0,

        // === Menge-Konfiguration ===
        mengeConfig: {
          hoursPerMenge: mengeConfigForProcess.hoursPerMenge || 1,
          mengePerDay: mengeConfigForProcess.mengePerDay || 1,
          efficiency: mengeConfigForProcess.efficiency || 100,
          setupTime: mengeConfigForProcess.setupTime || 0,
          cleanupTime: mengeConfigForProcess.cleanupTime || 0,
        },

        // === DISPLAY & UI DATA ===
        type: process.type || "process",
        color: process.color || "#1976d2",

        // === AUDIT & METADATA ===
        schedulingMethod: process.schedulingMethod || "manual",
        scheduledAt: process.scheduledAt || new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        // === CALENDAR CONTEXT ===
        calendarId: process.calendarId || instanceId,
        sourceTemplate: process.sourceTemplate || null,

        // === ADDITIONAL CONTEXT ===
        metadata: {
          transformedAt: new Date().toISOString(),
          transformVersion: "2.0",
          originalData: {
            hasInstanceId: Boolean(process.instanceId),
            hasCalendarId: Boolean(process.calendarId),
            hasChainId: Boolean(process.chainId),
            hasScheduledDate: Boolean(process.scheduledDate),
            hasMengeData: Boolean(
              process.mengeForDay || process.totalMengeForProcess
            ),
          },
        },
      };
    });
  };

  // Extract comprehensive employee information from process
  const extractEmployeeInformation = (process) => {
    const result = {
      names: [],
      fullData: [],
    };

    // Check multiple possible employee data sources
    if (
      process.names &&
      Array.isArray(process.names) &&
      process.names.length > 0
    ) {
      result.names = [...process.names];
    }

    if (process.assignedEmployees && Array.isArray(process.assignedEmployees)) {
      result.fullData = process.assignedEmployees.map((emp) => ({
        name: emp.name || emp.employee?.name || "",
        id: emp.id || emp.employee?.id || "",
        role: emp.role || "",
        machine: emp.machine || null,
        machineData: emp.machineData || null,
      }));

      // Extract names if not already present
      if (result.names.length === 0) {
        result.names = result.fullData.map((emp) => emp.name).filter(Boolean);
      }
    }

    // Fallback to single employee if names is not an array
    if (result.names.length === 0 && process.assignedEmployee) {
      result.names = [process.assignedEmployee];
    }

    return result;
  };

  // Extract comprehensive machine information from process
  const extractMachineInformation = (process) => {
    const result = {
      name: "",
      id: null,
      fullData: null,
    };

    // Check multiple possible machine data sources
    if (process.machinename) {
      result.name = process.machinename;
    }

    if (process.machine) {
      result.fullData = process.machine;
      result.name =
        result.name || process.machine.prozess || process.machine.name || "";
      result.id = process.machine._id || process.machine.id;
    }

    if (process.machineId) {
      result.id = process.machineId;
    }

    // Check assignedEmployees for machine data
    if (process.assignedEmployees && process.assignedEmployees.length > 0) {
      const empWithMachine = process.assignedEmployees.find(
        (emp) => emp.machine
      );
      if (empWithMachine && empWithMachine.machine) {
        result.fullData = empWithMachine.machine;
        result.name =
          result.name ||
          empWithMachine.machine.prozess ||
          empWithMachine.machine.name ||
          "";
        result.id =
          result.id || empWithMachine.machine._id || empWithMachine.machine.id;
      }
    }

    return result;
  };

  // Transform tasks for MongoDB
  const transformTasksForMongoDB = (tasks, selectedANR) => {
    return tasks.map((task, index) => ({
      // Core identification
      id: task.id || `task-${index}`,
      _id: task._id,
      instanceId:
        task.instanceId || `${task.id || "task"}-${index}-${Date.now()}`,

      // Task details
      description: task.description || task.title || "",
      title: task.title || task.description || "",
      hours: Math.max(0, task.hours || 0),

      // Assignment
      assignee: task.assignee || "",
      assigneeId: task.assigneeId || "",

      // Project data
      anr: task.anr || selectedANR?.anr || "",
      auftragsbezeichnung:
        task.auftragsbezeichnung || selectedANR?.auftragsbezeichnung || "",
      projectId: selectedANR?.projectId || "",

      // Scheduling
      scheduledDate: task.scheduledDate
        ? typeof task.scheduledDate === "string"
          ? task.scheduledDate
          : task.scheduledDate.toISOString()
        : null,

      // Status
      paid: task.paid !== undefined ? task.paid : true,
      completed: task.completed || false,

      // Chain relationship
      chainId: task.chainId || null,

      // Metadata
      type: "task",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
  };

  // Transform events for MongoDB
  const transformEventsForMongoDB = (events, selectedANR) => {
    return events.map((event, index) => ({
      // Core identification
      id: event.id || `event-${index}`,
      _id: event._id,
      instanceId:
        event.instanceId || `${event.id || "event"}-${index}-${Date.now()}`,

      // Event details
      title: event.title || event.description || "",
      description: event.description || event.title || "",

      // Project data
      anr: event.anr || selectedANR?.anr || "",
      auftragsbezeichnung:
        event.auftragsbezeichnung || selectedANR?.auftragsbezeichnung || "",
      projectId: selectedANR?.projectId || "",

      // Scheduling
      date: event.date || event.scheduledDate,
      scheduledDate: event.scheduledDate || event.date,

      // Metadata
      type: "event",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
  };

  // Validate transformed data before sending to MongoDB
  const validateTransformedData = (processes, tasks, events) => {
    const errors = [];
    const processTypes = {
      singleDay: 0,
      multiDay: 0,
      chains: 0,
      recurring: 0,
      withMenge: 0,
    };

    // Validate processes
    processes.forEach((process, index) => {
      if (!process.instanceId) {
        errors.push(`Process ${index + 1} missing instanceId`);
      }
      if (!process.label) {
        errors.push(`Process ${index + 1} missing label`);
      }

      // Count types
      if (process.isMultiDay) processTypes.multiDay++;
      else processTypes.singleDay++;

      if (process.chainId) processTypes.chains++;
      if (process.instanceNumber > 1) processTypes.recurring++;
      if (process.mengeForDay > 0) processTypes.withMenge++;
    });

    // Validate tasks
    tasks.forEach((task, index) => {
      if (!task.instanceId) {
        errors.push(`Task ${index + 1} missing instanceId`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      processTypes,
      summary: {
        processesCount: processes.length,
        tasksCount: tasks.length,
        eventsCount: events.length,
      },
    };
  };

  // Enhanced API call to save planning to MongoDB
  const savePlanningToMongoDB = async (payload) => {
    try {
      console.log("📡 Making enhanced API call to save planning");

      const response = await axios.post(
        `${base_url}/v1/CreateProjectPlanning`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          timeout: 60000, // 60 second timeout for large datasets
        }
      );

      if (response.status === 200 || response.status === 201) {
        return {
          success: true,
          data: response.data,
          message: "Planning saved successfully",
        };
      } else {
        return {
          success: false,
          error: `Unexpected response status: ${response.status}`,
          data: response.data,
        };
      }
    } catch (error) {
      console.error("❌ API Error:", error);
      return {
        success: false,
        error: error.message || "Unknown API error",
        details: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        },
      };
    }
  };

  // Get formatted error message for user display
  const getFormattedErrorMessage = (error) => {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 400:
          return "Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.";
        case 401:
          return "Authentifizierung fehlgeschlagen. Bitte aktualisieren Sie die Seite.";
        case 403:
          return "Keine Berechtigung zum Speichern.";
        case 404:
          return "Server-Endpunkt nicht gefunden.";
        case 500:
          return "Serverfehler. Bitte kontaktieren Sie den Support.";
        case 413:
          return "Daten zu groß. Versuchen Sie weniger Prozesse zu speichern.";
        case 408:
          return "Zeitüberschreitung. Bitte versuchen Sie es erneut.";
        default:
          return `Serverfehler (${status}): ${
            error.response.data?.message || "Unbekannt"
          }`;
      }
    } else if (error.request) {
      return "Netzwerkfehler. Überprüfen Sie Ihre Internetverbindung.";
    } else if (error.code === "ECONNABORTED") {
      return "Zeitüberschreitung der Anfrage. Versuchen Sie es erneut.";
    } else {
      return error.message || "Unbekannter Fehler beim Speichern.";
    }
  };

  // Handle Task Creation
  const handleCreateTask = (task) => {
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      if (!newCalendarData["unscheduled-tasks"]) {
        newCalendarData["unscheduled-tasks"] = {
          processes: [],
          tasks: [],
          events: [],
        };
      }
      newCalendarData["unscheduled-tasks"].tasks.push({
        ...task,
        scheduledDate: null,
      });
      return newCalendarData;
    });
    toast.success("Task created successfully!");
  };

  // Handle Event Creation
  const handleCreateEvent = (event) => {
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      if (event.date) {
        const dateKey = event.date.toISOString().split("T")[0];
        if (!newCalendarData[dateKey]) {
          newCalendarData[dateKey] = { processes: [], tasks: [], events: [] };
        }
        newCalendarData[dateKey].events.push(event);
      } else {
        if (!newCalendarData["unscheduled-events"]) {
          newCalendarData["unscheduled-events"] = {
            processes: [],
            tasks: [],
            events: [],
          };
        }
        newCalendarData["unscheduled-events"].events.push(event);
      }
      return newCalendarData;
    });
    toast.success("Event created successfully!");
  };

  // Handle Drop Task
  const handleDropTask = (task, dropDate, source) => {
    if (!task || !dropDate) return;

    const targetDateKey = dropDate.toISOString().split("T")[0];
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };

      // Initialize target date if not present
      if (!newCalendarData[targetDateKey]) {
        newCalendarData[targetDateKey] = {
          processes: [],
          tasks: [],
          events: [],
        };
      }

      // Find the broken chainId on this date
      const brokenChainsOnDate = newCalendarData[targetDateKey].processes
        .map((process) => process.chainId)
        .filter((chainId) => brokenChains[chainId]);

      const chainIdToAssign =
        brokenChainsOnDate.length > 0 ? brokenChainsOnDate[0] : null;

      if (chainIdToAssign) {
        task.chainId = chainIdToAssign;
      }

      // Add task to target date
      newCalendarData[targetDateKey].tasks.push({
        ...task,
        scheduledDate: dropDate,
        chainId: chainIdToAssign,
        type: ItemTypes.TASK,
      });

      // Remove task from source
      if (source === "unscheduled-tasks") {
        // Remove from unscheduled tasks
        newCalendarData["unscheduled-tasks"].tasks = newCalendarData[
          "unscheduled-tasks"
        ].tasks.filter((t) => t.id !== task.id);
      } else {
        // Remove from source date's tasks
        if (newCalendarData[source]) {
          newCalendarData[source].tasks = newCalendarData[source].tasks.filter(
            (t) => t.id !== task.id
          );
        }
      }

      return newCalendarData;
    });

    toast.success("Task scheduled successfully!");
  };

  // Handle Drop Event
  const handleDropEvent = (event, dropDate, source) => {
    if (!event || !dropDate) return;

    const targetDateKey = dropDate.toISOString().split("T")[0];
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };

      // Initialize target date if not present
      if (!newCalendarData[targetDateKey]) {
        newCalendarData[targetDateKey] = {
          processes: [],
          tasks: [],
          events: [],
        };
      }

      // Find the broken chainId on this date
      const brokenChainsOnDate = newCalendarData[targetDateKey].processes
        .map((process) => process.chainId)
        .filter((chainId) => brokenChains[chainId]);

      const chainIdToAssign =
        brokenChainsOnDate.length > 0 ? brokenChainsOnDate[0] : null;

      if (chainIdToAssign) {
        event.chainId = chainIdToAssign;
      }

      // Add event to target date
      newCalendarData[targetDateKey].events.push({
        ...event,
        date: dropDate,
        chainId: chainIdToAssign,
        type: ItemTypes.EVENT,
      });

      // Remove event from source
      if (source === "unscheduled-events") {
        // Remove from unscheduled events
        newCalendarData["unscheduled-events"].events = newCalendarData[
          "unscheduled-events"
        ].events.filter((e) => e.id !== event.id);
      } else {
        // Remove from source date's events
        if (newCalendarData[source]) {
          newCalendarData[source].events = newCalendarData[
            source
          ].events.filter((e) => e.id !== event.id);
        }
      }

      return newCalendarData;
    });

    toast.success("Event scheduled successfully!");
  };

  // Toggle Chain View
  const toggleChainView = (chainId) => {
    setCombinedChains((prev) => ({
      ...prev,
      [chainId]: !prev[chainId],
    }));
  };

  // Handle Process Duplication
  const handleDuplicateProcess = (process) => {
    const duplicatedProcess = {
      ...process,
      id: uuidv4(),
      label: `${process.label} (Copy)`,
      chainId: uuidv4(),
      type: ItemTypes.PROCESS,
    };
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      if (process.scheduledDate) {
        const dateKey = process.scheduledDate.toISOString().split("T")[0];
        if (!newCalendarData[dateKey]) {
          newCalendarData[dateKey] = { processes: [], tasks: [], events: [] };
        }
        newCalendarData[dateKey].processes.push(duplicatedProcess);
      }
      return newCalendarData;
    });
    toast.success("Process duplicated successfully!");
  };

  // Handle Task Duplication
  const handleDuplicateTask = (task) => {
    const duplicatedTask = {
      ...task,
      id: uuidv4(),
      chainId: task.chainId,
      type: ItemTypes.TASK,
    };
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      if (task.scheduledDate) {
        const dateKey = task.scheduledDate.toISOString().split("T")[0];
        if (!newCalendarData[dateKey]) {
          newCalendarData[dateKey] = { processes: [], tasks: [], events: [] };
        }
        newCalendarData[dateKey].tasks.push(duplicatedTask);
      } else {
        if (!newCalendarData["unscheduled-tasks"]) {
          newCalendarData["unscheduled-tasks"] = {
            processes: [],
            tasks: [],
            events: [],
          };
        }
        newCalendarData["unscheduled-tasks"].tasks.push({
          ...duplicatedTask,
          scheduledDate: null,
        });
      }
      return newCalendarData;
    });
    toast.success("Task duplicated successfully!");
  };

  // Handle Event Duplication
  const handleDuplicateEvent = (event) => {
    const duplicatedEvent = {
      ...event,
      id: uuidv4(),
      chainId: event.chainId,
      type: ItemTypes.EVENT,
    };
    setCalendarData((prevData) => {
      const newCalendarData = { ...prevData };
      if (event.date) {
        const dateKey = event.date.toISOString().split("T")[0];
        if (!newCalendarData[dateKey]) {
          newCalendarData[dateKey] = { processes: [], tasks: [], events: [] };
        }
        newCalendarData[dateKey].events.push(duplicatedEvent);
      } else {
        if (!newCalendarData["unscheduled-events"]) {
          newCalendarData["unscheduled-events"] = {
            processes: [],
            tasks: [],
            events: [],
          };
        }
        newCalendarData["unscheduled-events"].events.push({
          ...duplicatedEvent,
          date: null,
        });
      }
      return newCalendarData;
    });
    toast.success("Event duplicated successfully!");
  };

  // Combine Selected Processes into a Chain
  const combineSelectedProcesses = () => {
    if (selectedProcessIds.length < 2) {
      toast.error("Please select at least two processes to combine.");
      return;
    }

    const newChainId = `chain-${uuidv4()}`;

    // Get the selected processes
    const selectedProcesses = [];
    templates.forEach((template) => {
      template.processes.forEach((process) => {
        if (
          selectedProcessIds.includes(process.id) ||
          selectedProcessIds.includes(process._id)
        ) {
          selectedProcesses.push(process);
        }
      });
    });

    // Sort the selected processes by scheduledDate or some other criteria
    selectedProcesses.sort((a, b) => {
      if (a.scheduledDate && b.scheduledDate) {
        return a.scheduledDate - b.scheduledDate;
      } else {
        return 0;
      }
    });

    // Assign chainOrder
    selectedProcesses.forEach((process, index) => {
      process.chainOrder = index;
    });

    setTemplates((prevTemplates) => {
      return prevTemplates.map((template) => {
        const updatedProcesses = template.processes.map((process) => {
          if (
            selectedProcessIds.includes(process.id) ||
            selectedProcessIds.includes(process._id)
          ) {
            return {
              ...process,
              chainId: newChainId,
              chainOrder: selectedProcesses.find(
                (p) => p.id === process.id || p._id === process._id
              ).chainOrder,
            };
          }
          return process;
        });
        return {
          ...template,
          processes: updatedProcesses,
        };
      });
    });

    // Clear selection
    setSelectedProcessIds([]);
    toast.success("Processes combined into a chain successfully!");
  };

  // Break Chain Function
  const breakChain = (chainId) => {
    setBrokenChains((prev) => ({
      ...prev,
      [chainId]: true,
    }));
    toast.success("Chain broken successfully!");
  };

  // Toggle Chain Broken
  const toggleChainBrokenFunction = (chainId) => {
    if (brokenChains[chainId]) {
      // Repair the chain
      setBrokenChains((prev) => {
        const newBrokenChains = { ...prev };
        delete newBrokenChains[chainId];
        return newBrokenChains;
      });

      // Also unset combined view to let processes be visualized individually
      setCombinedChains((prev) => {
        const newCombinedChains = { ...prev };
        delete newCombinedChains[chainId];
        return newCombinedChains;
      });

      toast.success("Chain repaired successfully!");
    } else {
      // Break the chain
      breakChain(chainId);

      // Visual indication of the broken state by showing them individually
      setCombinedChains((prev) => {
        const newCombinedChains = { ...prev };
        delete newCombinedChains[chainId];
        return newCombinedChains;
      });
    }
  };

  function findProcessKey(proc, config) {
    return Object.keys(config).find(
      (key) => config[key].processId === proc._id
    );
  }

  function generateProcessInstances(
    proc,
    processKey,
    calculations,
    processInstancesList,
    dropDate
  ) {
    const calculation = processKey && calculations[processKey];
    console.log(`🔍 calcualtion ${calculation} with key ${processKey}`);
    if (!calculation) return [proc];

    return calculation.dayBreakdown.map((dayInfo) => {
      const idx = dayInfo.day;
      const instanceId = `${proc.id}-d${idx}`;

      const processInstance = {
        ...proc,
        instanceId,
        dropDate,
        hours: dayInfo.hours,
        hoursForThisDay: dayInfo.hours,
        dayNumber: dayInfo.day,
        totalDays: calculation.daysRequired,
        isMultiDay: calculation.daysRequired > 1,
        mengeForDay: dayInfo.menge,
        dayFraction: dayInfo.fraction,
        optimalFraction: dayInfo.optimalFraction.label,
        label: `${proc.label} (Day ${dayInfo.day}/${calculation.daysRequired})`,
      };

      processInstancesList.push(processInstance);
      return processInstance;
    });
  }

  const handleDropProcess = (droppedProcess, dropDate, chainId) => {
    if (!droppedProcess) {
      console.error("❌ droppedProcess is null or undefined");
      toast.error("Fehler: Prozess ist nicht definiert");
      return;
    }

    if (!dropDate) {
      console.error("❌ dropDate is null or undefined");
      toast.error("Fehler: Drop-Datum ist nicht definiert");
      return;
    }

    if (!droppedProcess.id && !droppedProcess._id) {
      console.error("❌ droppedProcess has no valid ID");
      toast.error("Fehler: Prozess hat keine gültige ID");
      return;
    }

    const workingDropDate = ensureWorkingDay(new Date(dropDate));
    console.log(
      `📅 Drop date adjusted: ${dropDate} → ${workingDropDate.toDateString()}`
    );

    // Find the template that contains this process
    const template = templates.find((t) =>
      t.processes.some((p) => p._id === droppedProcess._id)
    );

    console.log(
      "droppedProcess: ",
      droppedProcess,
      "templates : ",
      templates,
      "template:",
      template
    );
    if (!template) {
      toast.error("Template für den verschobenen Prozess nicht gefunden.");
      console.error("❌ Template not found for process:", droppedProcess);
      return;
    }

    try {
      let chainProcesses = template.processes.filter(
        (p) => p.chainId === chainId
      );

      // Assign chain order if missing
      const processInstances = [];
      if (!chainProcesses || chainProcesses.length < 1) {
        const processKey = findProcessKey(droppedProcess, processMengeConfig);
        chainProcesses = generateProcessInstances(
          droppedProcess,
          processKey,
          mengeCalculations,
          processInstances,
          dropDate
        );
      } else {
        chainProcesses = chainProcesses.map((proc, index) => {
          const processKey = findProcessKey(proc, processMengeConfig);
          return generateProcessInstances(
            proc,
            processKey,
            mengeCalculations,
            processInstances,
            dropDate
          );
        });
      }

      chainProcesses = chainProcesses.flat();
      chainProcesses.sort((a, b) => {
        if (a.chainOrder - b.chainOrder !== 0)
          return a.chainOrder - b.chainOrder;
        return a.halfDayIndex - b.halfDayIndex;
      });

      const isChainBroken = brokenChains[chainId];
      console.log(`🔗 Chain ${chainId} is ${isChainBroken} `, chainProcesses);

      if (isChainBroken || chainProcesses.length <= 1) {
        const availability = isDateAvailable(
          workingDropDate,
          droppedProcess.hours || 8,
          calendarData
        );
        scheduleSingleProcessEnhanced(droppedProcess, workingDropDate);
      } else {
        const scheduledDates = scheduleProcessesAroundProcess(
          chainProcesses,
          droppedProcess,
          workingDropDate,
          calendarData
        );

        // Validate scheduling results
        const validationIssues = validateSchedulingResults(
          scheduledDates,
          chainProcesses
        );

        // Apply conflict resolution
        const resolvedDates = resolveResourceConflicts(
          scheduledDates,
          calendarData
        );

        // Apply to calendar
        applyScheduledDatesToCalendar(scheduledDates, chainProcesses, chainId);

        if (validationIssues.length > 0) {
          toast.warning(
            `Kette geplant mit ${validationIssues.length} Warnungen - siehe Konsole für Details`
          );
        } else {
          toast.success(
            `Kette von ${chainProcesses.length} Prozessen erfolgreich geplant`
          );
        }
      }
    } catch (error) {
      console.error("❌ ERROR IN SCHEDULING:", {
        error: error,
        message: error.message,
        stack: error.stack,
        droppedProcess: droppedProcess?.label || droppedProcess?.id,
        chainId: chainId,
        dropDate: dropDate,
      });
      toast.error(
        `Fehler beim Planen der Prozesse: ${
          error.message || "Unbekannter Fehler"
        }`
      );
    }
  };

  const scheduleSingleProcessEnhanced = (process, dropDate) => {
    console.log(
      `📅 Scheduling single process: ${
        process.label
      } on ${dropDate.toDateString()}`
    );

    const dateKey = getISODateString(dropDate);

    setCalendarData((prevData) => {
      const newData = { ...prevData };

      // Remove process from any existing date using multiple ID checks
      Object.keys(newData).forEach((key) => {
        if (newData[key]?.processes) {
          newData[key].processes = newData[key].processes.filter((p) => {
            const matches =
              (p._id && p._id === process._id) ||
              (p.id && p.id === process.id) ||
              (p.instanceId && p.instanceId === process.instanceId) ||
              (p.calendarId && p.calendarId === process.calendarId);
            return !matches;
          });
        }
      });

      // Create entry for this date if needed
      if (!newData[dateKey]) {
        newData[dateKey] = { processes: [], tasks: [], events: [] };
      }

      // Add process to the date with enhanced metadata
      const processToAdd = {
        ...process,
        calendarId: `${process.id}-${
          process.chainId || "single"
        }-${Date.now()}`,
        scheduledDate: new Date(dropDate),
        type: ItemTypes.PROCESS,
        assignedEmployees: process.assignedEmployees || [],
        names: process.names || [],
        machinename: process.machinename || "",
        // Add scheduling metadata
        scheduledAt: new Date().toISOString(),
        schedulingMethod: "single-enhanced",
      };

      newData[dateKey].processes.push(processToAdd);
      return newData;
    });

    toast.success(`Prozess "${process.label}" erfolgreich geplant`);
  };

  // Apply scheduled dates to calendar with comprehensive conflict handling
  const applyScheduledDatesToCalendar = (
    scheduledDates,
    chainProcesses,
    chainId
  ) => {
    console.log("📅 Applying scheduled dates to calendar", scheduledDates);

    setCalendarData((prevData) => {
      const newData = { ...prevData };

      // Remove all chain processes from their current dates
      Object.keys(newData).forEach((key) => {
        if (newData[key]?.processes) {
          newData[key].processes = newData[key].processes.filter((p) => {
            // Remove if it's part of this chain
            return !chainProcesses.some(
              (cp) =>
                (cp._id && cp._id === p._id) ||
                (cp.id && cp.id === p.id) ||
                p.chainId === chainId
            );
          });
        }
      });

      // Add all scheduled processes to their new dates
      Object.entries(scheduledDates).forEach(([processId, scheduleInfo]) => {
        const dateKey = getISODateString(scheduleInfo.date);

        // Find the original process
        const originalProcess = chainProcesses.find((p) => {
          const pid = p._id || p.id;
          const instanceId = p.instanceId || "";
          return (
            pid + instanceId === processId ||
            (scheduleInfo.originalProcessId &&
              (p._id || p.id) === scheduleInfo.originalProcessId)
          );
        });

        if (!originalProcess) {
          console.warn(`⚠️ Original process not found for ${processId}`);
          return;
        }

        // Create entry for this date if needed
        if (!newData[dateKey]) {
          newData[dateKey] = { processes: [], tasks: [], events: [] };
        }

        // Create the process to add
        const processToAdd = {
          ...originalProcess,
          ...scheduleInfo.process,
          calendarId:
            scheduleInfo.process?.calendarId ||
            `${processId}-${chainId}-${Date.now()}`,
          scheduledDate: scheduleInfo.date,
          type: ItemTypes.PROCESS,
          chainId: chainId,
          // Preserve chain metadata
          chainOffset: scheduleInfo.chainOffset,
          schedulingMethod: "chain-enhanced",
          scheduledAt: new Date().toISOString(),
        };

        newData[dateKey].processes.push(processToAdd);
        console.log(`✅ Added ${processToAdd.label} to ${dateKey}`);
      });

      return newData;
    });
    setMengeConfigModalOpen(false);
  };

  const validateSchedulingResults = (scheduledDates, originalProcesses) => {
    const issues = [];

    // Check 1: All processes are scheduled
    if (Object.keys(scheduledDates).length !== originalProcesses.length) {
      issues.push(
        `Process count mismatch: Expected ${originalProcesses.length}, got ${
          Object.keys(scheduledDates).length
        }`
      );
    }

    // Check 2: No weekend scheduling
    Object.entries(scheduledDates).forEach(([processId, scheduleInfo]) => {
      if (isWeekend(scheduleInfo.date)) {
        issues.push(
          `Process ${processId} scheduled on weekend: ${scheduleInfo.date.toDateString()}`
        );
      }
    });

    // Check 3: No overlapping resources beyond 8 hours per day
    const dateHours = {};
    Object.entries(scheduledDates).forEach(([processId, scheduleInfo]) => {
      const dateKey = scheduleInfo.date.toISOString().split("T")[0];
      dateHours[dateKey] = (dateHours[dateKey] || 0) + scheduleInfo.hours;
    });

    Object.entries(dateHours).forEach(([dateKey, totalHours]) => {
      if (totalHours > 8) {
        issues.push(
          `Date ${dateKey} overloaded: ${totalHours} hours scheduled`
        );
      }
    });

    // Check 4: Chain ordering preserved
    const chainGroups = {};
    Object.entries(scheduledDates).forEach(([processId, scheduleInfo]) => {
      const process = originalProcesses.find(
        (p) => (p._id || p.id) === processId
      );
      if (process && process.chainId) {
        if (!chainGroups[process.chainId]) {
          chainGroups[process.chainId] = [];
        }
        chainGroups[process.chainId].push({
          processId,
          chainOrder: process.chainOrder || 0,
          date: scheduleInfo.date,
        });
      }
    });

    Object.entries(chainGroups).forEach(([chainId, processes]) => {
      const sorted = processes.sort((a, b) => a.chainOrder - b.chainOrder);
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].date < sorted[i - 1].date) {
          issues.push(
            `Chain ${chainId} ordering violation: Process ${
              sorted[i].processId
            } scheduled before ${sorted[i - 1].processId}`
          );
        }
      }
    });

    if (issues.length > 0) {
      console.warn("⚠️ Scheduling validation issues:", issues);
    } else {
      console.log("✅ Scheduling validation passed");
    }

    return issues;
  };

  // Change month
  const handlePrevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev === 0) {
        setCurrentYear((prev) => prev - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev === 11) {
        setCurrentYear((prev) => prev + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const handleCurrentMonth = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  // ========== FILTER FUNCTIONS ==========
  const filterProcesses = (processes) => {
    return processes.filter((process) => {
      // Machine filter
      if (activeFilters.machines.length > 0) {
        if (!activeFilters.machines.includes(process.machineId)) {
          return false;
        }
      }

      // Employee filter
      if (activeFilters.employees.length > 0) {
        if (!activeFilters.employees.includes(process.employeeId)) {
          return false;
        }
      }

      // Process type filter
      if (activeFilters.processTypes.length > 0) {
        if (!activeFilters.processTypes.includes(process.type)) {
          return false;
        }
      }

      // Chain status filter
      if (activeFilters.chainStatus.length > 0) {
        const hasChain = process.chainId !== null;
        const isBroken = brokenChains[process.chainId];

        if (activeFilters.chainStatus.includes("chained") && !hasChain) {
          return false;
        }
        if (activeFilters.chainStatus.includes("broken") && !isBroken) {
          return false;
        }
        if (activeFilters.chainStatus.includes("unchained") && hasChain) {
          return false;
        }
      }

      return true;
    });
  };

  // Apply filters to calendar data
  const filteredCalendarData = useMemo(() => {
    const filtered = {};

    Object.keys(calendarData).forEach((dateKey) => {
      const dayData = calendarData[dateKey];

      filtered[dateKey] = {
        ...dayData,
        processes: filterProcesses(dayData.processes || []),
        tasks: dayData.tasks || [],
        events: dayData.events || [],
      };
    });

    return filtered;
  }, [calendarData, activeFilters, brokenChains]);

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    setActiveFilters({
      machines: [],
      employees: [],
      processTypes: [],
      chainStatus: [],
    });
  };
  // ========== END FILTER FUNCTIONS ==========

  // ========== BATCH OPERATION HANDLERS ==========
  const toggleBatchMode = () => {
    setBatchMode(!batchMode);
    if (batchMode) {
      setSelectedProcessIdsForBatch(new Set());
    }
  };

  const handleProcessSelect = (processId) => {
    setSelectedProcessIdsForBatch((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(processId)) {
        newSet.delete(processId);
      } else {
        newSet.add(processId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allProcessIds = new Set();
    Object.values(filteredCalendarData).forEach((dayData) => {
      dayData.processes?.forEach((process) => {
        allProcessIds.add(process.id || process._id);
      });
    });
    setSelectedProcessIdsForBatch(allProcessIds);
  };

  const handleDeselectAll = () => {
    setSelectedProcessIdsForBatch(new Set());
  };

  const handleBatchChain = () => {
    const selectedProcesses = [];
    Object.values(filteredCalendarData).forEach((dayData) => {
      dayData.processes?.forEach((process) => {
        if (selectedProcessIdsForBatch.has(process.id || process._id)) {
          selectedProcesses.push(process);
        }
      });
    });

    if (selectedProcesses.length < 2) {
      toast.error("Wählen Sie mindestens 2 Prozesse zum Verketten aus");
      return;
    }

    const newChainId = `chain-${uuidv4()}`;

    // Sort by date
    selectedProcesses.sort((a, b) => {
      if (a.scheduledDate && b.scheduledDate) {
        return a.scheduledDate - b.scheduledDate;
      }
      return 0;
    });

    // Assign chain order
    selectedProcesses.forEach((process, index) => {
      process.chainOrder = index;
      process.chainId = newChainId;
    });

    // Update templates
    setTemplates((prevTemplates) => {
      return prevTemplates.map((template) => {
        const updatedProcesses = template.processes.map((process) => {
          const selected = selectedProcesses.find(
            (sp) => (sp.id || sp._id) === (process.id || process._id)
          );
          if (selected) {
            return {
              ...process,
              chainId: newChainId,
              chainOrder: selected.chainOrder,
            };
          }
          return process;
        });
        return { ...template, processes: updatedProcesses };
      });
    });

    setSelectedProcessIdsForBatch(new Set());
    setBatchMode(false);
    toast.success(`${selectedProcesses.length} Prozesse verkettet`);
  };

  const handleBatchDelete = () => {
    if (
      window.confirm(
        `${selectedProcessIdsForBatch.size} Prozesse wirklich löschen?`
      )
    ) {
      selectedProcessIdsForBatch.forEach((processId) => {
        handleDeleteProcess(processId);
      });

      setSelectedProcessIdsForBatch(new Set());
      setBatchMode(false);
      toast.success("Ausgewählte Prozesse gelöscht");
    }
  };

  const handleBatchAssignEmployee = () => {
    toast.info("Mitarbeiterzuweisung wird implementiert");
  };

  const handleBatchAssignMachine = () => {
    toast.info("Maschinenzuweisung wird implementiert");
  };
  // ========== END BATCH HANDLERS ==========

  // ========== CHAIN PREVIEW HELPERS ==========
  const getAllProcessesInChain = (chainId) => {
    const chainProcesses = [];

    templates.forEach((template) => {
      template.processes?.forEach((process) => {
        if (process.chainId === chainId) {
          chainProcesses.push(process);
        }
      });
    });

    return chainProcesses.sort(
      (a, b) => (a.chainOrder || 0) - (b.chainOrder || 0)
    );
  };

  const setChainPreviewData = (process) => {
    if (!process.chainId || brokenChains[process.chainId]) {
      setChainPreview(null);
      setIsDraggingChainProcess(false);
      return;
    }

    const allProcesses = getAllProcessesInChain(process.chainId);
    const offsets = calculateChainOffsets(allProcesses, filteredCalendarData);

    setChainPreview({
      chainId: process.chainId,
      draggedProcess: process,
      allProcesses,
      offsets,
    });
    setIsDraggingChainProcess(true);
  };

  const clearChainPreview = () => {
    setChainPreview(null);
    setIsDraggingChainProcess(false);
    setHoverDate(null);
  };
  // ========== END CHAIN PREVIEW HELPERS ==========

  // ========== TEMPLATE SELECTION HANDLERS ==========
  const handleDateClick = (date) => {
    setSelectedDateForTemplate(date);
    setTemplateSelectionModalOpen(true);
  };

  const handleSelectTemplateForApply = (template) => {
    setSelectedTemplateForApply(template);
    setSelectedAnrForTemplate(template.anr || ""); // Pre-fill with template's ANR if available
  };

  const handleApplyTemplateToDate = async (modalData) => {
    // Get data either from modal callback or from state
    const templateToApply = modalData?.template || selectedTemplateForApply;
    const anrToApply = modalData?.anr || selectedAnrForTemplate;
    const anrData = modalData?.anrData || selectedAnrData;

    if (!selectedDateForTemplate || !templateToApply) {
      toast.error("Please select a date and template");
      return;
    }

    if (!anrToApply) {
      toast.error("Please select an ANR");
      return;
    }

    try {
      // Set the selected ANR for saving
      if (anrData) {
        setSelectedANR(anrData);
        setSelectedAnrData(anrData);
        // Fetch project Menge data for the selected ANR
        fetchProjectMengeData(anrData);
      }

      const dateKey = getISODateString(selectedDateForTemplate);
      let currentDate = new Date(selectedDateForTemplate);
      let processCount = 0;

      // Add each process from the template to the calendar
      for (const process of templateToApply.processes || []) {
        // Skip weekends
        while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
          currentDate.setDate(currentDate.getDate() + 1);
        }

        const targetDateKey = getISODateString(currentDate);

        // Generate a unique ID for this process instance
        const uniqueId = `${
          process.id || process._id || "process"
        }-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create process instance for calendar with ANR assigned
        const processInstance = {
          ...process,
          id: uniqueId,
          _id: process._id || uniqueId,
          scheduledDate: new Date(currentDate), // Must be Date object for .getTime()
          source: "template",
          templateId: templateToApply._id,
          templateName: templateToApply.name,
          originalProcessId: process.id || process._id,
          anr: anrToApply, // Assign the selected ANR
        };

        // Add to calendar data directly
        setCalendarData((prevData) => {
          const newData = { ...prevData };
          if (!newData[targetDateKey]) {
            newData[targetDateKey] = { processes: [], tasks: [], events: [] };
          }
          if (!newData[targetDateKey].processes) {
            newData[targetDateKey].processes = [];
          }
          newData[targetDateKey].processes.push(processInstance);
          return newData;
        });

        processCount++;

        // Move to next working day for next process
        currentDate.setDate(currentDate.getDate() + 1);
      }

      toast.success(
        `Template "${templateToApply.name}" applied with ANR ${anrToApply}! ${processCount} processes added.`
      );
      setTemplateSelectionModalOpen(false);
      setSelectedDateForTemplate(null);
      setSelectedTemplateForApply(null);
      setSelectedAnrForTemplate("");
      setSelectedAnrData(null);
    } catch (error) {
      console.error("Error applying template:", error);
      toast.error("Failed to apply template: " + error.message);
    }
  };
  // ========== END TEMPLATE SELECTION HANDLERS ==========

  return (
    <DndProvider backend={HTML5Backend}>
      {/* MAIN CONTAINER WITH NEW LAYOUT */}
      <Box
        className="calendarMainContainer"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* ========== TOP BAR ========== */}
        <Box className="calendarTopBar" sx={{ flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Produktionskalender
            </Typography>

            {/* Template Selector (Compact) */}
            <CompactTemplateSelector
              templates={templates}
              expandedTemplates={expandedTemplates}
              setExpandedTemplates={setExpandedTemplates}
              onDeleteTemplate={deleteTemplate}
              onCopyTemplate={handleCopyTemplate}
              openAnrModalForTemplate={openAnrModalForTemplate}
            />
          </Box>

          <Box className="topBarControls" sx={{ display: "flex", gap: 1.5 }}>
            {/* Batch Mode Toggle */}
            <BatchModeToggle
              batchMode={batchMode}
              onToggle={toggleBatchMode}
              selectedCount={selectedProcessIdsForBatch.size}
            />

            {/* Refresh */}
            <Tooltip title="Aktualisieren">
              <IconButton
                color="inherit"
                size="small"
                onClick={() => window.location.reload()}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ========== QUICK FILTERS BAR ========== */}
        <Box sx={{ flexShrink: 0 }}>
          <QuickFiltersBar
            machines={machines}
            employees={employees}
            processTypes={[
              { value: "fertigung", label: "Fertigung" },
              { value: "montage", label: "Montage" },
              { value: "qualitat", label: "Qualität" },
              { value: "endmontage", label: "Endmontage" },
            ]}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
          />
        </Box>

        {/* ========== CALENDAR CONTAINER (FULL WIDTH) ========== */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Month Navigation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              bgcolor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h5" fontWeight="bold">
                {monthNames[currentMonth]} {currentYear}
              </Typography>
              <Box>
                <IconButton color="primary" onClick={handlePrevMonth}>
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton color="primary" onClick={handleCurrentMonth}>
                  <TodayIcon />
                </IconButton>
                <IconButton color="primary" onClick={handleNextMonth}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Calendar */}
          <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            <EnhancedCalendar
              currentMonth={currentMonth}
              currentYear={currentYear}
              calendarData={filteredCalendarData}
              onDropProcess={handleDropProcess}
              onDropTask={handleDropTask}
              onDropEvent={handleDropEvent}
              attendanceData={attendanceData}
              machines={machines}
              updateProcessEmployee={updateProcessEmployee}
              updateProcessMachine={updateProcessMachine}
              combinedChains={combinedChains}
              toggleChainView={toggleChainView}
              brokenChains={brokenChains}
              toggleChainBroken={toggleChainBrokenFunction}
              handleDuplicateTask={handleDuplicateTask}
              handleDuplicateEvent={handleDuplicateEvent}
              employees={employees}
              onDeleteProcess={handleDeleteProcess}
              onSplitProcess={handleSplitProcess}
              onMonthChange={(month, year) => {
                setCurrentMonth(month);
                setCurrentYear(year);
              }}
              openMengeConfigForProcess={openMengeConfigForProcess}
              projectMengeData={projectMengeData}
              batchMode={batchMode}
              selectedProcessIds={selectedProcessIdsForBatch}
              onProcessSelect={handleProcessSelect}
              setChainPreviewData={setChainPreviewData}
              clearChainPreview={clearChainPreview}
              setHoverDate={setHoverDate}
              onDateClick={handleDateClick}
            />
          </Box>
        </Box>

        {/* Combine Button */}
        {selectedProcessIds.length >= 2 && (
          <Box sx={{ position: "fixed", bottom: 80, right: 24, zIndex: 1100 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={combineSelectedProcesses}
              startIcon={<ChainIcon />}
              size="large"
            >
              Combine {selectedProcessIds.length} Selected Processes
            </Button>
          </Box>
        )}

        {/* ========== CHAIN PREVIEW OVERLAY ========== */}
        {isDraggingChainProcess && (
          <ChainPreviewOverlay
            chainPreview={chainPreview}
            currentHoverDate={hoverDate}
            calendarData={filteredCalendarData}
            addWorkingDays={addWorkingDays}
            getISODateString={getISODateString}
          />
        )}

        {/* ========== BATCH OPERATIONS BAR ========== */}
        {batchMode && selectedProcessIdsForBatch.size > 0 && (
          <BatchOperationsBar
            selectedCount={selectedProcessIdsForBatch.size}
            totalProcesses={Object.values(filteredCalendarData).reduce(
              (sum, day) => sum + (day.processes?.length || 0),
              0
            )}
            onChain={handleBatchChain}
            onDelete={handleBatchDelete}
            onAssignEmployee={handleBatchAssignEmployee}
            onAssignMachine={handleBatchAssignMachine}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onCancel={() => {
              setBatchMode(false);
              setSelectedProcessIdsForBatch(new Set());
            }}
          />
        )}

        {/* ========== COMPACT TASKS/EVENTS DRAWER ========== */}
        <CompactTasksEventsDrawer
          open={tasksDrawerOpen}
          onClose={() => setTasksDrawerOpen(false)}
          onToggle={() => setTasksDrawerOpen(!tasksDrawerOpen)}
          calendarData={calendarData}
          TaskBox={TaskBox}
          EventBox={EventBox}
          handleDuplicateTask={handleDuplicateTask}
          handleDuplicateEvent={handleDuplicateEvent}
          setTaskModalOpen={setTaskModalOpen}
          setEventModalOpen={setEventModalOpen}
        />

        {/* ========== FLOATING SAVE BUTTON ========== */}
        <Tooltip title="Plan speichern">
          <Fab
            color="primary"
            sx={{
              position: "fixed",
              bottom: 24,
              left: 24,
              zIndex: 1100,
            }}
            onClick={handleSave}
          >
            <SaveIcon />
          </Fab>
        </Tooltip>
      </Box>

      {/* ========== ALL EXISTING MODALS (keep as-is) ========== */}
      <ANRModal
        open={anrModalOpen}
        onClose={() => {
          setAnrModalOpen(false);
          setAnrAssignTarget(null);
          setAnrAssignData(null);
        }}
        onSelect={handleAnrSelect}
      />

      <TaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onCreate={handleCreateTask}
        employees={employees}
        groups={groups}
        openAnrModalForTask={openAnrModalForTask}
        assignedTaskAnrs={assignedTaskAnrs}
      />

      <EventModal
        open={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        onCreate={handleCreateEvent}
      />

      <MengeConfigurationModal
        open={mengeConfigModalOpen}
        onClose={() => {
          setMengeConfigModalOpen(false);
          setSelectedProcessForMenge(null);
        }}
        selectedProcess={selectedProcessForMenge}
        projectMengeData={projectMengeData}
        currentConfig={
          selectedProcessForMenge
            ? processMengeConfig[
                `${selectedProcessForMenge.id}-${selectedProcessForMenge.label}`
              ]
            : null
        }
        DAY_FRACTIONS={DAY_FRACTIONS}
        onConfigUpdate={updateProcessMengeConfig}
        onApplyToCalendar={handleDropProcess}
        mengeCalculations={mengeCalculations}
        selectedDayFraction={selectedDayFraction}
        setSelectedDayFraction={setSelectedDayFraction}
      />

      <CopyTemplateModal
        open={copyModalOpen}
        onClose={() => {
          setCopyModalOpen(false);
          setTemplateToCopy(null);
        }}
        onCopy={performCopyTemplate}
      />

      {/* Template Selection Modal with Pagination */}
      <TemplateApplyModal
        open={templateSelectionModalOpen}
        onClose={() => {
          setTemplateSelectionModalOpen(false);
          setSelectedDateForTemplate(null);
          setSelectedTemplateForApply(null);
          setSelectedAnrForTemplate("");
          setSelectedAnrData(null);
        }}
        templates={templates}
        selectedDateForTemplate={selectedDateForTemplate}
        onApplyTemplate={handleApplyTemplateToDate}
        getISODateString={getISODateString}
      />

      <ToastContainer position="bottom-right" theme="colored" />
    </DndProvider>
  );
};

export default CombinedCalendarMain;
